import { useState, useEffect, useCallback, useContext, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, OPERATION_NAMES, SURFACE_PROCESSES, AuthContext, useProcesses } from "../App";
import Layout from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import { OperationGuard } from "../components/OperationGuard";
import SmartDeleteModal from "../components/SmartDeleteModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, Trash, Check, Printer, PencilSimple, CaretDown, CaretRight, MagnifyingGlass, ArrowRight, ArrowCounterClockwise, X, BellRinging, DownloadSimple } from "@phosphor-icons/react";
import { exportWithHeader } from "../utils/excelExport";
import { DefectTable, DEFECT_TYPES, REASON_6M } from "../components/DefectTable";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const REJECTION_REASONS = DEFECT_TYPES;
const MAX_VALUE = 999999;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_factory-flow-63/artifacts/fswlmzn0_6d091b38-eb5c-4e7f-89d1-83b1081dd8c3.jpeg";
const OPERATION_ORDER_DISPLAY = ["Cutting", "Incoming Store", "Machining P1", "Machining P2", "Semi-Firewall", "Painting", "Welding", "Silver Plating", "Outsource", "Firewall", "Dispatch"];
const OPERATION_FLOW = [
  { key: "cutting", label: "Cutting" },
  { key: "incoming_store", label: "Incoming Store" },
  { key: "machining_p1", label: "Machining P1" },
  { key: "machining_p2", label: "Machining P2" },
  { key: "semi_firewall", label: "Semi-Firewall" },
  { key: "painting", label: "Painting" },
  { key: "silver_plating", label: "Silver Plating" },
  { key: "welding", label: "Welding" },
  { key: "outsource", label: "Outsource" },
  { key: "firewall", label: "Firewall" },
  { key: "dispatch", label: "Dispatch" }
];

function Operations() {
  const f2 = (v) => { const num = parseFloat(v); return isNaN(num) ? '0.00' : num.toFixed(2); };
  const { type } = useParams();
  const { user } = useContext(AuthContext);
  // Live per-tenant operation list (sidebar / dropdowns reflect Customization → Processes)
  const { opNames: tenantOpNames, allOps: tenantAllOps, processes: tenantProcesses } = useProcesses();

  // Live operation flow — derived from per-tenant processes (Customization).
  // Falls back to the hardcoded OPERATION_FLOW above when no custom processes
  // exist yet. This makes "Issued To?" dialogs reflect what the user actually
  // configured.
  const liveOpFlow = (tenantProcesses && tenantProcesses.length)
    ? tenantProcesses.map(p => ({
        key: p.code,
        label: p.name || p.code.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      }))
    : OPERATION_FLOW;
  const isAdmin = user?.role === "Admin";
  // Identify the current process from the tenant's customized list and use
  // its `behaviour` field to drive which special UI is rendered. This makes
  // it work even when a default process is deleted and re-created with a
  // different code — as long as the behaviour is set, the right sheet shows.
  const currentProc = (tenantProcesses || []).find(p => p.code === type) || {};
  const behaviour = currentProc.behaviour || type; // fallback to code for legacy
  const isCutting = behaviour === "cutting" || type === "cutting";
  const isFirewall = behaviour === "firewall" || type === "firewall";
  const isOutsource = behaviour === "outsource" || type === "outsource";
  const isDispatch = behaviour === "dispatch" || type === "dispatch";
  const isIncomingStore = behaviour === "incoming_store" || type === "incoming_store";
  const isAssembly = behaviour === "assembly";
  const isMachining = type === "machining_p1" || type === "machining_p2";
  const machiningPlant = type === "machining_p1" ? "P1" : type === "machining_p2" ? "P2" : null;
  const isSemiFinish = behaviour === "semi_firewall" || type === "semi_firewall";
  const opName = tenantOpNames[type] || OPERATION_NAMES[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const prodLabel = (isSemiFinish || isFirewall || isDispatch || isIncomingStore) ? 'Inspected' : 'Produced';

  const [parts, setParts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [outsourceProcessesList, setOutsourceProcessesList] = useState(SURFACE_PROCESSES);
  const [selectedPart, setSelectedPart] = useState(null);
  // Company info (name + logo) — read once and used in all printed slips so
  // every tenant prints with their own branding.
  const [companyInfo, setCompanyInfo] = useState({ name: "", logo_url: "" });
  useEffect(() => {
    axios.get(`${API}/company/info`).then(r => setCompanyInfo(r.data || {})).catch(() => {});
  }, []);
  const slipLogoUrl = (() => {
    const raw = companyInfo.logo_url || "/swami-icon.png";
    return raw.startsWith("http") ? raw : `${window.location.origin}${raw}`;
  })();
  const slipCompanyName = companyInfo.name || "Manufacturing Tracker";

  // Dispatch PO table — exposed via ref so submitTransaction can save its edits.
  const dispatchPORef = useRef(null);
  const [reasonDialog, setReasonDialog] = useState(null);
  const [isApproved, setIsApproved] = useState(false); // kept for potential future use
  const [rejectionDetails, setRejectionDetails] = useState([]);
  const [reworkDetails, setReworkDetails] = useState([]);
  const [partSearchOpen, setPartSearchOpen] = useState(false);
  const [partSearchQuery, setPartSearchQuery] = useState("");
  const [issueToDialog, setIssueToDialog] = useState(false);
  const [issueToOperation, setIssueToOperation] = useState("");
  const [reworkToDialog, setReworkToDialog] = useState(false);
  const [reworkToOperation, setReworkToOperation] = useState("");
  const [returnAsIsDialog, setReturnAsIsDialog] = useState(false);
  const [returnAsSources, setReturnAsSources] = useState([]);
  const [returnAsSelections, setReturnAsSelections] = useState({});
  const [returnAsLoading, setReturnAsLoading] = useState(false);
  const [incomingMode, setIncomingMode] = useState("in_house");
  // Semi-Firewall outsource checkbox
  const [outsourceChecked, setOutsourceChecked] = useState(false);
  const [outsourceToDialog, setOutsourceToDialog] = useState(false);
  const [outsourceToSupplier, setOutsourceToSupplier] = useState("");
  const [outsourceToProcess, setOutsourceToProcess] = useState("");
  // Outsource sheet: supplier selection + received from semi-fw
  const [outsourceSupplier, setOutsourceSupplier] = useState("");
  const [outsourceProcess, setOutsourceProcess] = useState("");
  const [outsourceReceivedQty, setOutsourceReceivedQty] = useState(0);
  const [outsourceReceivedIds, setOutsourceReceivedIds] = useState([]);
  const [outsourceReceivedDetails, setOutsourceReceivedDetails] = useState([]);
  // Delete password dialog
  const [deleteDialog, setDeleteDialog] = useState(null); // {transId, dataId}
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [smartDeleteTxnId, setSmartDeleteTxnId] = useState(null);
  // Edit mode — uses the same form, just loads existing data
  const [editingTransId, setEditingTransId] = useState(null);
  // KG↔Nos bidirectional sync tracker
  const [lastRecvEditField, setLastRecvEditField] = useState("kg"); // "kg" or "nos"
  // Return to stock
  const [returnDialog, setReturnDialog] = useState(null); // {trans}
  const [returnQty, setReturnQty] = useState("");
  const [returnTo, setReturnTo] = useState("");
  const [returnApprove, setReturnApprove] = useState(false);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [approveReturnDialog, setApproveReturnDialog] = useState(null);
  const [printChoiceDialog, setPrintChoiceDialog] = useState(null); // transaction for print choice
  const [invoiceDialog, setInvoiceDialog] = useState(null); // dispatch invoice dialog state
  const [invoiceForm, setInvoiceForm] = useState({
    qty: "", customer: "", customer_address: "", customer_gstin: "",
    rate: "", gst_percent: "18", notes: "",
    company_name: "", company_address: "", company_gstin: "", company_phone: "", company_email: "",
  });
  // Machining P1: Proceed Finishing to P2
  const [proceedToP2, setProceedToP2] = useState(false);
  // Notification sidebar for incoming issues
  const [notifOpen, setNotifOpen] = useState(false);
  const [incomingAll, setIncomingAll] = useState([]);
  const [incomingCount, setIncomingCount] = useState(0);
  const [selectedNotifs, setSelectedNotifs] = useState(new Set());

  const emptyForm = {
    part_id: "", part_no: "", date: new Date().toISOString().split('T')[0],
    opening_balance: 0, received_qty: 0, received_qty_nos: 0,
    total_qty: 0, total_stock_nos: 0, produced_qty: 0, ok_qty: 0,
    rejected_qty: 0, rework_qty: 0,
    issued_qty: 0, issued_balance: 0, balanced_qty_issued: 0,
    balance_qty: 0, balanced_qty_produced: 0, carry_forward_balance: 0,
    rejection_reason_type: "", rejection_remarks: "",
    rework_reason_type: "", rework_remarks: "",
    supplier: "", process: "",
    total_received_qty: 0, cumulative_received_qty: 0,
    supplier_stock: 0,
    return_stock: 0, rework_stock: 0, return_as_is: false,
    remarks: ""
  };
  const [formData, setFormData] = useState(emptyForm);

  const fetchParts = useCallback(async () => {
    try {
      let allParts = (await axios.get(`${API}/parts`)).data;
      // Fix #4: STRICT filter — only show parts where this operation is explicitly enabled
      const checkEnabled = (p) => {
        const oe = p.operations_enabled || {};
        // For outsource: show all parts (any part can be outsourced for cutting, machining, or surface processes)
        if (type === 'outsource') {
          return true;
        }
        // For machining_p1/p2, also check legacy 'machining' key
        if (type === 'machining_p1' || type === 'machining_p2') {
          return oe[type] === true || oe['machining'] === true;
        }
        return oe[type] === true;
      };
      if (machiningPlant) {
        // Machining P1/P2: show parts based on operations_enabled only (no machining_location filter)
        allParts = allParts.filter(checkEnabled);
      } else {
        allParts = allParts.filter(checkEnabled);
      }
      // ── Company-Part-No pooling ──────────────────────────────────────
      // When multiple customer parts share a `company_part_no`, the shop
      // floor should see ONE pooled entry (not N customer rows). Collapse
      // them here: keep the FIRST part_id in the group as canonical, swap
      // the display labels to the Company Part No, and stash the list of
      // customer parts on the row for tooltips / detail views.
      // EXCEPT for the Dispatch screen — that one is customer-facing and
      // must keep customer-part rows visible (each PO is per customer).
      if (type !== 'dispatch') {
        const pooled = new Map();
        for (const p of allParts) {
          const cpn = (p.company_part_no || p.part_no || "").trim();
          if (!cpn) continue;
          if (!pooled.has(cpn)) {
            pooled.set(cpn, {
              ...p,
              // Show Company Part No as the primary label.
              part_no: cpn,
              _customer_part_no: p.part_no,
              _customer: p.customer || "",
              customer_parts: [{ part_no: p.part_no, customer: p.customer || "" }],
              is_pooled: false,
              _pool_ids: [p.id],
            });
          } else {
            const agg = pooled.get(cpn);
            agg.is_pooled = true;
            agg.customer_parts.push({ part_no: p.part_no, customer: p.customer || "" });
            agg._pool_ids.push(p.id);
            // Combine customer names with separator
            agg.customer = agg.customer ? `${agg.customer} / ${p.customer || ""}` : (p.customer || "");
          }
        }
        allParts = Array.from(pooled.values());
      }
      setParts(allParts);
    } catch { toast.error("Failed to fetch parts"); }
  }, [type, machiningPlant]);

  const fetchTransactions = useCallback(async () => {
    try {
      if (formData.part_id) {
        const params = new URLSearchParams({ operation: type, part_id: formData.part_id });
        setTransactions((await axios.get(`${API}/transactions?${params}`)).data);
      } else {
        // No part selected — show latest transaction per part
        setTransactions((await axios.get(`${API}/transactions/latest-per-part?operation=${type}&include_rollover=true`)).data);
      }
    }
    catch {}
  }, [type, formData.part_id]);

  const fetchSuppliers = useCallback(async () => {
    try { setSuppliers((await axios.get(`${API}/suppliers`)).data); } catch {}
  }, []);

  // Per-company outsource processes (replaces hardcoded SURFACE_PROCESSES)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/outsource-processes`, { skipPeriod: true });
        if (cancelled) return;
        const codes = (r.data?.items || []).map(it => it.code).filter(Boolean);
        if (codes.length) setOutsourceProcessesList(codes);
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pending issued approvals
  const [pendingIssued, setPendingIssued] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState("");
  const [approvalChecked, setApprovalChecked] = useState(false);

  // Receipt entries replaced by incoming issues (direct flow, no approval)
  const [incomingIssuesQty, setIncomingIssuesQty] = useState(0);
  const [incomingIssueIds, setIncomingIssueIds] = useState({ op: [], rm: [] });
  const [incomingDetails, setIncomingDetails] = useState([]);
  const [prevCumulativeReceived, setPrevCumulativeReceived] = useState(0);
  // Assembly: BOM availability preview ({all_sufficient, lines:[]})
  const [bomAvailability, setBomAvailability] = useState(null);

  const fetchBomAvailability = useCallback(async (partId, qty) => {
    if (!isAssembly || !partId || !qty || qty <= 0) { setBomAvailability(null); return; }
    try {
      const res = await axios.get(`${API}/assembly/availability/${partId}?qty=${qty}`);
      setBomAvailability(res.data || null);
    } catch { setBomAvailability(null); }
  }, [isAssembly]);

  // Trigger BOM availability re-check whenever produced_qty changes
  useEffect(() => {
    if (!isAssembly || !selectedPart) return;
    const q = parseFloat(formData.produced_qty) || 0;
    const t = setTimeout(() => fetchBomAvailability(selectedPart.id, q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.produced_qty, selectedPart?.id, isAssembly]);

  const fetchPendingIssued = useCallback(async (partId) => {
    if (isCutting || !partId) { setPendingIssued([]); return; }
    try {
      const res = await axios.get(`${API}/transactions/pending-issued/${partId}/${type}`);
      setPendingIssued(res.data || []);
    } catch { setPendingIssued([]); }
  }, [type, isCutting]);

  const fetchIncomingIssues = useCallback(async (partId) => {
    if (!partId) return;
    try {
      const res = await axios.get(`${API}/transactions/incoming-issues/${partId}/${type}`);
      // Pool-aware net (cutting): received minus already issued from cutting
      // by any pool member. Other ops: pool_already_issued_* is 0.
      const grossRecv = res.data.total_received_qty || 0;
      const poolIssuedKg = res.data.pool_already_issued_kg || 0;
      const netRecv = Math.max(0, grossRecv - poolIssuedKg);
      setIncomingIssuesQty(netRecv);
      setIncomingIssueIds({ op: res.data.op_issue_ids || [], rm: res.data.rm_issue_ids || [] });
      setIncomingDetails(res.data.details || []);
    } catch {
      setIncomingIssuesQty(0);
      setIncomingIssueIds({ op: [], rm: [] });
      setIncomingDetails([]);
    }
  }, [type]);

  const fetchPendingReturns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transactions/pending-returns/${type}`);
      setPendingReturns(res.data || []);
    } catch { setPendingReturns([]); }
  }, [type]);

  const fetchIncomingNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transactions/all-incoming/${type}`);
      setIncomingAll(res.data.items || []);
      setIncomingCount(res.data.count || 0);
    } catch { setIncomingAll([]); setIncomingCount(0); }
  }, [type]);

  useEffect(() => {
    fetchParts();
    fetchTransactions();
    fetchPendingReturns();
    fetchIncomingNotifications();
    if (isOutsource || isIncomingStore || isSemiFinish) fetchSuppliers();
    const interval = setInterval(() => {
      fetchParts();
      fetchTransactions();
      fetchIncomingNotifications();
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchParts, fetchTransactions, fetchPendingReturns, fetchIncomingNotifications, fetchSuppliers, isOutsource, isIncomingStore, isSemiFinish]);

  // Auto-calculate derived values — DUAL BALANCE SYSTEM
  // L = Balance Production (Nos) = D - E (unprocessed material)
  // K_bal = Balance Issue (Nos) = H + I - J (produced but not issued)
  // M = Carry Forward = (L + K_bal) × GW for cutting, L + K_bal for others
  useEffect(() => {
    setFormData(prev => {
      const opening = parseFloat(prev.opening_balance) || 0;
      const totalRecvQty = parseFloat(prev.total_received_qty) || 0;
      const produced = parseFloat(prev.produced_qty) || 0;
      const rejected = parseFloat(prev.rejected_qty) || 0;
      const rework = parseFloat(prev.rework_qty) || 0;
      const issued = parseFloat(prev.issued_qty) || 0;
      const issuedBal = parseFloat(prev.issued_balance) || 0; // I = prev K_bal
      const gw = selectedPart?.gross_weight || 0;

      // Cumulative received = prevCumulative (K) + totalRecvQty
      const cumulativeRecv = prevCumulativeReceived + totalRecvQty;

      // Dispatch simplified
      if (isDispatch) {
        const returnStock = parseFloat(prev.return_stock) || 0;
        const reworkStock = parseFloat(prev.rework_stock) || 0;
        const available = opening + totalRecvQty + returnStock + reworkStock;
        const balanceQty = Math.max(0, available - issued);
        return { ...prev, total_stock_nos: available, balanced_qty_produced: balanceQty,
          cumulative_received_qty: cumulativeRecv, received_qty_nos: totalRecvQty };
      }

      // Outsource NEW: a=opening, b=received(from semi-fw), c=prev balance issue, d=issued, e=b+c-d, f=a+d
      if (isOutsource) {
        const b = totalRecvQty;          // received from semi-firewall
        const c = parseFloat(prev.issued_balance) || 0;  // previous balance issue
        const d = issued;
        const e = b + c - d;             // balance issued
        const f = opening + d;           // supplier stock
        return { ...prev,
          total_stock_nos: b + c,        // available to issue = b + c
          ok_qty: 0,
          balanced_qty_issued: e,         // balance issued (E)
          balanced_qty_produced: e,       // for display compatibility
          supplier_stock: f,              // (F) supplier stock
          carry_forward_balance: f,       // next opening = supplier stock
          cumulative_received_qty: cumulativeRecv,
          received_qty_nos: totalRecvQty
        };
      }

      // H = OK Quantity = E - F - G
      const ok = Math.max(0, produced - rejected - rework);
      const returnStock = parseFloat(prev.return_stock) || 0;
      const reworkStock = parseFloat(prev.rework_stock) || 0;

      if (isCutting && gw > 0) {
        // Bidirectional KG ↔ Nos sync — display to 2dp
        let recvKg, recvNos;
        if (lastRecvEditField === "nos") {
          recvNos = parseFloat(prev.received_qty_nos) || 0;
          recvKg = recvNos * gw;
        } else {
          recvKg = totalRecvQty;
          recvNos = gw > 0 ? parseFloat((totalRecvQty / gw).toFixed(2)) : 0;
        }
        const totalStockNos = gw > 0 ? parseFloat(((opening + recvKg) / gw + returnStock + reworkStock).toFixed(2)) : 0;
        const balanceIssue = parseFloat((ok + issuedBal - issued).toFixed(2));
        const balanceProduction = parseFloat((totalStockNos - produced).toFixed(2));
        const carryForward = parseFloat(((balanceProduction + balanceIssue) * gw).toFixed(2));

        return {
          ...prev,
          total_received_qty: recvKg,
          received_qty: recvKg,
          received_qty_nos: recvNos,
          total_stock_nos: totalStockNos,
          ok_qty: ok,
          balanced_qty_issued: balanceIssue,
          balanced_qty_produced: balanceProduction,
          carry_forward_balance: carryForward,
          cumulative_received_qty: prevCumulativeReceived + recvKg
        };
      }

      // ALL OTHER OPERATIONS (Nos-based)
      // Total Stock = Opening + Received + Return Stock + Rework Stock
      const totalStockNos = parseFloat((opening + totalRecvQty + returnStock + reworkStock).toFixed(2));
      const balanceIssue = parseFloat((ok + issuedBal - issued).toFixed(2));
      const balanceProduction = parseFloat((totalStockNos - produced).toFixed(2));
      const carryForward = parseFloat((balanceProduction + balanceIssue).toFixed(2));

      return {
        ...prev,
        ok_qty: ok,
        total_qty: totalStockNos,
        total_stock_nos: totalStockNos,
        received_qty_nos: totalRecvQty,
        balanced_qty_issued: balanceIssue,
        balanced_qty_produced: balanceProduction,
        carry_forward_balance: carryForward,
        cumulative_received_qty: cumulativeRecv
      };
    });
  }, [formData.produced_qty, formData.rejected_qty, formData.rework_qty,
      formData.issued_qty, formData.opening_balance,
      formData.issued_balance, formData.total_received_qty, formData.received_qty_nos,
      formData.return_stock, formData.rework_stock,
      isFirewall, isCutting, isDispatch, isOutsource, selectedPart, prevCumulativeReceived, lastRecvEditField]);

  // Sync rejection/rework qty from detail table rows
  useEffect(() => {
    const total = rejectionDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
    setFormData(prev => ({ ...prev, rejected_qty: total }));
  }, [rejectionDetails]);

  useEffect(() => {
    const total = reworkDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
    setFormData(prev => ({ ...prev, rework_qty: total }));
  }, [reworkDetails]);


  // Total Received Quantity change handler (used by all operations)
  const onTotalReceivedChange = (val) => {
    setFormData(prev => ({ ...prev, total_received_qty: val }));
  };


  const handlePartChange = async (partId) => {
    const part = parts.find(p => p.id === partId);
    if (!part) return;
    setSelectedPart(part);

    // 1+2. Fetch opening balance AND incoming issues in PARALLEL (Promise.all)
    // — was sequential before, costing ~2× round-trip latency on Atlas.
    let openBal = 0;
    let prevBalIssue = 0;
    let prevCumRecv = 0;
    let returnStock = 0;
    let reworkStock = 0;
    let autoRecvQty = 0;
    const [openRes, issRes] = await Promise.all([
      axios.get(`${API}/transactions/opening-balance/${partId}/${type}`).catch(() => null),
      axios.get(`${API}/transactions/incoming-issues/${partId}/${type}`).catch(() => null),
    ]);

    if (openRes && openRes.data) {
      openBal = openRes.data.opening_balance || 0;
      prevBalIssue = openRes.data.prev_balance_issue || 0;
      prevCumRecv = openRes.data.prev_cumulative_received || 0;
      returnStock = openRes.data.return_stock || 0;
      reworkStock = openRes.data.rework_stock || 0;
    } else {
      openBal = (part.opening_balances || {})[type] || 0;
    }

    setPrevCumulativeReceived(prevCumRecv);

    // Backend's `total_received_qty` is already NET (specific-mode allocations
    // included as-is, pool-mode reduced by pool_already_issued_kg). Frontend
    // must NOT subtract again — that double-counts and zeroes out split
    // allocations.
    if (issRes && issRes.data) {
      autoRecvQty = issRes.data.total_received_qty || 0;
      setIncomingIssuesQty(autoRecvQty);
      setIncomingIssueIds({ op: issRes.data.op_issue_ids || [], rm: issRes.data.rm_issue_ids || [] });
      setIncomingDetails(issRes.data.details || []);
    } else {
      setIncomingIssuesQty(0);
      setIncomingIssueIds({ op: [], rm: [] });
      setIncomingDetails([]);
    }

    setFormData(prev => ({
      ...prev,
      part_id: partId, part_no: part.part_no,
      opening_balance: openBal,
      total_received_qty: autoRecvQty,  // Pre-populate from incoming issues
      received_qty_nos: 0,
      received_qty: 0,
      issued_balance: prevBalIssue,  // I = Previous K_bal
      return_stock: returnStock,
      rework_stock: reworkStock
    }));

    setSelectedApproval("");
    setApprovalChecked(false);
    setIsApproved(false);
    fetchPendingIssued(partId);
  };

  // Outsource: fetch opening + received when part + supplier selected
  const fetchOutsourceData = async (partId, supplier, process) => {
    if (!partId || !supplier) return;
    try {
      const params = new URLSearchParams({ supplier, process: process || "" });
      const [openRes, recvRes] = await Promise.all([
        axios.get(`${API}/transactions/outsource-opening/${partId}?${params}`),
        axios.get(`${API}/transactions/outsource-received/${partId}?${params}`)
      ]);
      const openBal = openRes.data.opening_balance || 0;
      const prevBalIssue = openRes.data.prev_balance_issue || 0;
      const recvQty = recvRes.data.total_received_qty || 0;
      setOutsourceReceivedQty(recvQty);
      setOutsourceReceivedIds(recvRes.data.issue_ids || []);
      setOutsourceReceivedDetails(recvRes.data.details || []);
      setFormData(prev => ({
        ...prev,
        opening_balance: openBal,
        total_received_qty: recvQty,
        issued_balance: prevBalIssue
      }));
    } catch {
      setOutsourceReceivedQty(0);
      setOutsourceReceivedIds([]);
      setOutsourceReceivedDetails([]);
    }
  };

  const validateForm = () => {
    if (isDispatch) {
      const dispatched = parseFloat(formData.issued_qty) || 0;
      const available = (parseFloat(formData.opening_balance) || 0) + (parseFloat(formData.received_qty_nos) || 0);
      if (dispatched > available) {
        toast.error(`Dispatched Quantity (${dispatched}) cannot exceed Available Quantity (${available})`);
        return false;
      }
      if (dispatched < 0) { toast.error("Dispatched Quantity cannot be negative"); return false; }
      return true;
    }
    if (isOutsource) {
      const issued = parseFloat(formData.issued_qty) || 0;
      const received = parseFloat(formData.total_received_qty) || 0;
      const prevBalIssue = parseFloat(formData.issued_balance) || 0;
      const available = received + prevBalIssue;
      if (issued > available) {
        toast.error(`Issued Quantity (${issued}) cannot be greater than Received + Previous Balance Issue (${available})`);
        return false;
      }
      if (issued < 0) { toast.error("Issued Quantity cannot be negative"); return false; }
      return true;
    }
    if (!isDispatch) {
      const issued = parseFloat(formData.issued_qty) || 0;
      const issuedBal = parseFloat(formData.issued_balance) || 0;
      const ok = parseFloat(formData.ok_qty) || 0;
      if (issued > issuedBal + ok) {
        toast.error(`Issued Quantity (${issued}) cannot exceed Issued Balance + OK Quantity (${issuedBal + ok})`);
        return false;
      }
    }
    // No negative balance
    if ((parseFloat(formData.balanced_qty_produced) || 0) < 0) {
      toast.error("Balanced Quantity cannot be negative. Check your quantities.");
      return false;
    }
    return true;
  };

  // ONLY submit via button click, not Enter
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    // Compute totals directly from detail rows (not stale formData)
    const rejected = rejectionDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
    const rework = reworkDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);

    // Validate rejection details table (each row must have qty and reason)
    if (rejected > 0 && rejectionDetails.length > 0) {
      const missingRej = rejectionDetails.some(d => !d.qty || parseFloat(d.qty) <= 0 || !d.defect_type);
      if (missingRej) {
        toast.error("Each rejection row must have a Quantity and Reason");
        return;
      }
    }
    // Validate rework details table
    if (rework > 0 && reworkDetails.length > 0) {
      const missingRwk = reworkDetails.some(d => !d.qty || parseFloat(d.qty) <= 0 || !d.defect_type);
      if (missingRwk) {
        toast.error("Each rework row must have a Quantity and Reason");
        return;
      }
    }

    // If rework qty > 0, show "Rework To" dialog
    if (rework > 0 && !reworkToOperation) {
      setReworkToDialog(true);
      return;
    }

    // If issued qty > 0 and not dispatch, show "Issue To" dialog
    const issuedQty = parseFloat(formData.issued_qty) || 0;
    if (issuedQty > 0 && !isDispatch && !isOutsource && !issueToOperation) {
      // Semi-Firewall or Incoming Store with outsource checked → show outsource-to dialog instead
      if ((isSemiFinish || isIncomingStore) && outsourceChecked) {
        if (!outsourceToSupplier) {
          setOutsourceToDialog(true);
          return;
        }
      } else {
        // Normal "Issue To" dialog
        const currentIdx = liveOpFlow.findIndex(o => o.key === type);
        const nextOp = currentIdx >= 0 && currentIdx < liveOpFlow.length - 1 ? liveOpFlow[currentIdx + 1].key : "";
        setIssueToOperation(nextOp);
        setIssueToDialog(true);
        return;
      }
    }

    await submitTransaction();
  };

  const handleIssueToSubmit = async () => {
    setIssueToDialog(false);
    await submitTransaction();
  };

  const handleReworkToSubmit = async () => {
    setReworkToDialog(false);
    // Now re-trigger handleSubmit flow (reworkToOperation is set, so it won't loop)
    const issuedQty = parseFloat(formData.issued_qty) || 0;
    if (issuedQty > 0 && !isDispatch && !isOutsource && !issueToOperation) {
      if ((isSemiFinish || isIncomingStore) && outsourceChecked) {
        if (!outsourceToSupplier) { setOutsourceToDialog(true); return; }
      } else {
        const currentIdx = liveOpFlow.findIndex(o => o.key === type);
        const nextOp = currentIdx >= 0 && currentIdx < liveOpFlow.length - 1 ? liveOpFlow[currentIdx + 1].key : "";
        setIssueToOperation(nextOp);
        setIssueToDialog(true);
        return;
      }
    }
    await submitTransaction();
  };

  const handleOutsourceToSubmit = async () => {
    if (!outsourceToSupplier) { toast.error("Select a supplier"); return; }
    setOutsourceToDialog(false);
    await submitTransaction();
  };

  const submitTransaction = async (retryCount = 0) => {
    try {
      const payload = { ...formData, operation: type };
      // Ensure rejection/rework qty is computed directly from rows
      payload.rejected_qty = rejectionDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
      payload.rework_qty = reworkDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
      // Recalculate OK qty
      payload.ok_qty = Math.max(0, (parseFloat(payload.produced_qty) || 0) - payload.rejected_qty - payload.rework_qty);
      if (selectedApproval && approvalChecked) {
        payload.approved_transaction_id = selectedApproval;
      }
      for (const k of ['opening_balance','received_qty','received_qty_nos','produced_qty','ok_qty',
        'rejected_qty','rework_qty','issued_qty','issued_balance','balanced_qty_issued',
        'balance_qty','balanced_qty_produced','carry_forward_balance','total_qty','total_stock_nos',
        'total_received_qty','cumulative_received_qty','return_stock','rework_stock']) {
        payload[k] = parseFloat(payload[k]) || 0;
      }
      payload.return_as_is = !!formData.return_as_is;
      if (payload.balanced_qty_produced < 0) payload.balanced_qty_produced = 0;
      if (payload.balanced_qty_issued < 0) payload.balanced_qty_issued = 0;

      // Add issued_to target if issuing
      if (issueToOperation && issueToOperation !== "__none__") {
        payload.issued_to = issueToOperation;
      } else if (editingTransId) {
        // Editing: explicitly clear issued_to if set to none
        payload.issued_to = "";
      }

      // ── Per-part Process Flow validation ──
      // If the part has its own operations_flow defined, check that the
      // issued_to target matches the next step. enforce_flow_strict=true
      // turns the warning into a hard block.
      if (payload.issued_to && payload.issued_to !== "__none__") {
        const partDoc = parts.find(pt => pt.id === payload.part_id);
        const flow = partDoc?.operations_flow;
        if (Array.isArray(flow) && flow.length > 0) {
          const curIdx = flow.indexOf(type);
          if (curIdx !== -1) {
            const expected = curIdx + 1 < flow.length ? flow[curIdx + 1] : null;
            if (expected && expected !== payload.issued_to) {
              const expLabel = OPERATION_NAMES[expected] || expected;
              const gotLabel = OPERATION_NAMES[payload.issued_to] || payload.issued_to;
              if (partDoc.enforce_flow_strict) {
                toast.error(
                  `❌ Strict flow: must issue ${partDoc.part_no} to '${expLabel}' next, not '${gotLabel}'.`,
                  { duration: 6000 }
                );
                return;
              }
              const proceed = window.confirm(
                `⚠ Process Flow Warning\n\n` +
                `Part '${partDoc.part_no}' has a defined flow.\n` +
                `Expected next step:  ${expLabel}\n` +
                `You're issuing to:    ${gotLabel}\n\n` +
                `Proceed anyway?`
              );
              if (!proceed) return;
            }
          }
        }
      }
      // Add rework_to target if rework
      if (reworkToOperation) {
        payload.rework_to = reworkToOperation;
      }
      // Add incoming mode for incoming store
      if (isIncomingStore) {
        payload.incoming_mode = incomingMode;
      }

      // Semi-Firewall / Incoming Store outsource: add flags so outsource sheet can find this issue
      if ((isSemiFinish || isIncomingStore) && outsourceChecked && (parseFloat(formData.issued_qty) || 0) > 0) {
        payload.is_outsource = true;
        payload.outsource_supplier = outsourceToSupplier;
        payload.outsource_process = outsourceToProcess;
      }

      // Machining P1: Proceed Finishing to P2
      if (type === "machining_p1" && proceedToP2) {
        payload.proceed_to_p2 = true;
      }

      // Outsource sheet: add consumed outsource IDs and supplier_stock
      if (isOutsource && outsourceReceivedIds.length > 0) {
        payload.consumed_outsource_ids = outsourceReceivedIds;
      }
      if (isOutsource) {
        payload.supplier_stock = parseFloat(formData.supplier_stock) || 0;
      }

      // Include consumed issue IDs so backend marks them consumed
      if (incomingIssueIds.op.length > 0) {
        payload.consumed_op_issue_ids = incomingIssueIds.op;
      }
      if (incomingIssueIds.rm.length > 0) {
        payload.consumed_rm_issue_ids = incomingIssueIds.rm;
      }

      // Include rejection/rework details
      if (rejectionDetails.length > 0) payload.rejection_details = rejectionDetails;
      if (reworkDetails.length > 0) payload.rework_details = reworkDetails;

      // Assembly: pre-flight component shortage check + attach snapshot
      if (isAssembly) {
        if (bomAvailability && !bomAvailability.all_sufficient) {
          const shorts = bomAvailability.lines.filter(l => !l.sufficient)
            .map(l => `${l.component_part_no} short by ${l.shortage}`).join(", ");
          toast.error(`Cannot save — components short: ${shorts}`);
          return;
        }
        if (bomAvailability && bomAvailability.lines && bomAvailability.lines.length > 0) {
          payload.consumed_components = bomAvailability.lines.map(l => ({
            component_type: l.component_type || "manufactured",
            component_part_id: l.component_part_id,
            component_bought_out_id: l.component_bought_out_id,
            component_part_no: l.component_part_no,
            component_description: l.component_description,
            qty_per_assembly: l.qty_per_assembly,
            qty_consumed: l.required_qty,
            consume_from_operation: l.consume_from_operation,
          }));
        }
      }
      // Set primary reason from first detail row
      if (rejectionDetails.length > 0 && rejectionDetails[0].defect_type) {
        payload.rejection_reason_type = rejectionDetails[0].defect_type;
        payload.rejection_remarks = rejectionDetails.map((d,i) => `${i+1}. ${d.defect_type} (${d.qty || 0})${d.remarks ? ': '+d.remarks : ''}`).join('; ');
      }
      if (reworkDetails.length > 0 && reworkDetails[0].defect_type) {
        payload.rework_reason_type = reworkDetails[0].defect_type;
        payload.rework_remarks = reworkDetails.map((d,i) => `${i+1}. ${d.defect_type} (${d.qty || 0})${d.remarks ? ': '+d.remarks : ''}`).join('; ');
      }

      // Remove empty string fields that could cause validation issues
      for (const k of Object.keys(payload)) {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      }

      let res;
      if (editingTransId) {
        // UPDATE existing transaction — no new record
        res = await axios.put(`${API}/transactions/${editingTransId}`, payload);
        toast.success(`Transaction updated (${res.data.data_id || 'OK'}) — balances recalculated`);
        setLastSavedTransId(editingTransId);
        setEditingTransId(null);
      } else {
        // CREATE new transaction
        res = await axios.post(`${API}/transactions`, payload);
        toast.success(`Transaction saved (${res.data.data_id || 'OK'})`);
        setLastSavedTransId(res.data.id);
      }

      // Dispatch: persist any pending Dispatch Quantity 2 edits in the open-PO table.
      if (isDispatch && dispatchPORef.current?.hasDirty?.()) {
        try {
          const out = await dispatchPORef.current.saveAll();
          if (out?.saved > 0) toast.success(`Updated ${out.saved} PO row(s)`);
        } catch { /* errors already toasted in DispatchPOTable */ }
      }

      // Auto-push rework stock to target operation
      const reworkQtyTotal = reworkDetails.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
      if (reworkQtyTotal > 0 && reworkToOperation) {
        try {
          await axios.post(`${API}/transactions/push-rework-stock`, {
            transaction_id: res.data.id || editingTransId,
            target_operation: reworkToOperation,
            qty: reworkQtyTotal
          });
          toast.success(`${reworkQtyTotal} rework stock pushed to ${OPERATION_NAMES[reworkToOperation] || reworkToOperation}`);
        } catch { /* non-blocking */ }
      }

      // If issued_qty > 0 and "Issue To" target was selected, just notify
      // The target operation will see this via "Add Receipt from Previous Operation" dropdown
      const issuedQty = parseFloat(formData.issued_qty) || 0;
      if (issuedQty > 0 && issueToOperation && !isDispatch) {
        const targetLabel = liveOpFlow.find(o => o.key === issueToOperation)?.label || issueToOperation;
        toast.success(`${issuedQty} Nos issued to ${targetLabel} — receiver must approve in their operation`);
      }
      setIssueToOperation("");
      setReworkToOperation("");
      setOutsourceChecked(false);
      setOutsourceToSupplier("");
      setOutsourceToProcess("");
      setOutsourceReceivedQty(0);
      setOutsourceReceivedIds([]);
      setOutsourceReceivedDetails([]);
      setProceedToP2(false);

      fetchTransactions();
      // Reset form — clear part selection so it doesn't stay on the last one
      setFormData(emptyForm);
      setSelectedPart(null);
      setSelectedApproval("");
      setApprovalChecked(false);
      setIsApproved(false);
      setRejectionDetails([]);
      setReworkDetails([]);
      setIncomingIssuesQty(0);
      setIncomingIssueIds({ op: [], rm: [] });
      setIncomingDetails([]);
      setPrevCumulativeReceived(0);
    } catch (error) {
      // Auto-retry once on network/timeout errors
      if (retryCount < 1 && (!error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')) {
        console.warn('Save failed (network), retrying...', error.message);
        toast.info("Retrying save...");
        return submitTransaction(retryCount + 1);
      }
      // Extract detailed error message
      const errData = error.response?.data;
      let msg = "Failed to save data";
      if (errData?.detail) {
        msg = typeof errData.detail === 'string' ? errData.detail
          : Array.isArray(errData.detail) ? errData.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ')
          : JSON.stringify(errData.detail);
      } else if (error.message) {
        msg = error.message;
      }
      console.error('Transaction save error:', { status: error.response?.status, data: errData, message: error.message });
      toast.error(msg);
    }
  };

  const handleReasonSubmit = () => { submitTransaction(); };

  const handleDeleteTrans = async (transId) => {
    // Open password dialog instead of window.confirm
    const t = transactions.find(tr => tr.id === transId);
    setDeleteDialog({ transId, dataId: t?.data_id || transId.slice(0, 8) });
    setDeletePassword("");
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/transactions/${deleteDialog.transId}/verify-delete`, { password: deletePassword });
      toast.success("Transaction deleted & balances recalculated");
      setDeleteDialog(null);
      setDeletePassword("");
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const handleApproveConfirm = () => {
    if (!selectedApproval || !approvalChecked) {
      toast.error("Select a pending qty and tick the checkbox to approve");
      return;
    }
    const item = pendingIssued.find(p => p.id === selectedApproval);
    if (!item) return;
    toast.success(`Approved ${item.issued_qty} Nos from ${item.operation_name}`);
    setSelectedApproval("");
    setApprovalChecked(false);
  };

  // Return to stock handler
  const handleReturnToStock = (t) => {
    const currentIdx = liveOpFlow.findIndex(o => o.key === type);
    const prevOp = currentIdx > 0 ? liveOpFlow[currentIdx - 1].key : "";
    setReturnDialog(t);
    setReturnQty("");
    setReturnTo(prevOp);
    setReturnApprove(false);
  };

  const handleReturnConfirm = async () => {
    if (!returnDialog || !returnQty || parseFloat(returnQty) <= 0 || !returnTo || !returnApprove) {
      toast.error("Fill all fields and check approve"); return;
    }
    try {
      await axios.post(`${API}/transactions/return-to-stock`, {
        transaction_id: returnDialog.id,
        return_qty: parseFloat(returnQty),
        return_to: returnTo,
      });
      toast.success(`Return of ${returnQty} sent for approval`);
      setReturnDialog(null);
      fetchTransactions();
      fetchPendingReturns();
    } catch (e) { toast.error(e.response?.data?.detail || "Return failed"); }
  };

  const handleApproveReturn = async (returnId) => {
    try {
      await axios.post(`${API}/transactions/approve-return/${returnId}`);
      toast.success("Return approved & balances recalculated");
      setApproveReturnDialog(null);
      fetchTransactions();
      fetchPendingReturns();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const handleRejectReturn = async (returnId) => {
    try {
      await axios.post(`${API}/transactions/reject-return/${returnId}`);
      toast.success("Return rejected");
      setApproveReturnDialog(null);
      fetchPendingReturns();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  // Full-form edit handler — loads transaction data into the main form
  const handleEditTransaction = (t) => {
    // Find the part for this transaction
    const part = parts.find(p => p.id === t.part_id);
    if (part) setSelectedPart(part);

    setEditingTransId(t.id);

    // Populate all form fields from the transaction
    setFormData({
      part_id: t.part_id || "",
      part_no: t.part_no || "",
      date: t.date || new Date().toISOString().split('T')[0],
      opening_balance: t.opening_balance ?? 0,
      received_qty: t.received_qty ?? 0,
      received_qty_nos: t.received_qty_nos ?? 0,
      total_qty: t.total_qty ?? t.total_stock_nos ?? 0,
      total_stock_nos: t.total_stock_nos ?? 0,
      produced_qty: t.produced_qty ?? 0,
      ok_qty: t.ok_qty ?? 0,
      rejected_qty: t.rejected_qty ?? 0,
      rework_qty: t.rework_qty ?? 0,
      issued_qty: t.issued_qty ?? 0,
      issued_balance: t.issued_balance ?? 0,
      balanced_qty_issued: t.balanced_qty_issued ?? 0,
      balance_qty: t.balance_qty ?? 0,
      balanced_qty_produced: t.balanced_qty_produced ?? 0,
      carry_forward_balance: t.carry_forward_balance ?? 0,
      rejection_reason_type: t.rejection_reason_type || "",
      rejection_remarks: t.rejection_remarks || "",
      rework_reason_type: t.rework_reason_type || "",
      rework_remarks: t.rework_remarks || "",
      supplier: t.supplier || "",
      process: t.process || "",
      total_received_qty: t.total_received_qty ?? t.received_qty ?? 0,
      cumulative_received_qty: t.cumulative_received_qty ?? 0,
      supplier_stock: t.supplier_stock ?? 0,
      issued_to: t.issued_to || "",
      issued_from_data_id: t.issued_from_data_id || "",
      return_stock: t.return_stock ?? 0,
      rework_stock: t.rework_stock ?? 0,
      return_as_is: t.return_as_is || false,
      remarks: t.remarks || ""
    });

    // Set previous cumulative (cumulative - received of THIS transaction)
    const thisRecv = t.total_received_qty ?? t.received_qty ?? 0;
    setPrevCumulativeReceived(Math.max(0, (t.cumulative_received_qty || 0) - thisRecv));

    // Set issued_balance from transaction
    // For outsource, also set supplier/process
    if (isOutsource) {
      setOutsourceSupplier(t.supplier || "");
      setOutsourceProcess(t.process || "");
    }

    // For Semi-Firewall / Incoming Store, restore outsource state if transaction was outsourced
    if ((isSemiFinish || isIncomingStore) && t.is_outsource) {
      setOutsourceChecked(true);
      setOutsourceToSupplier(t.outsource_supplier || "");
      setOutsourceToProcess(t.outsource_process || "");
    }

    // For Machining P1, restore proceed_to_p2
    if (type === "machining_p1" && t.proceed_to_p2) {
      setProceedToP2(true);
    }

    // Set rejection/rework details
    setRejectionDetails(t.rejection_details || []);
    setReworkDetails(t.rework_details || []);

    // Set issued_to if present
    if (t.issued_to) setIssueToOperation(t.issued_to);

    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Editing transaction ${t.data_id || t.id.slice(0,8)} — modify fields and click Update Sheet`);
  };

  const cancelEdit = () => {
    setEditingTransId(null);
    setFormData(emptyForm);
    setSelectedPart(null);
    setRejectionDetails([]);
    setReworkDetails([]);
    setPrevCumulativeReceived(0);
    setIssueToOperation("");
    toast.info("Edit cancelled");
  };

  // Clear selected incoming notifications (frontend-side dismissal only).
  // We DO NOT mark issues as consumed in the database — that would also
  // remove them from the new-entry form's auto-fetch (Total Received Quantity),
  // which is exactly what the user reported losing. The qty is only truly
  // consumed when a receive transaction is saved at the destination
  // (via consumed_op_issue_ids in /transactions POST).
  const [dismissedNotifIds, setDismissedNotifIds] = useState(new Set());
  const handleClearNotifications = async () => {
    if (selectedNotifs.size === 0) { toast.error("Select at least one item"); return; }
    setDismissedNotifIds(prev => new Set([...prev, ...selectedNotifs]));
    toast.success(`${selectedNotifs.size} item(s) dismissed (qty still available in new-entry form)`);
    setSelectedNotifs(new Set());
  };

  // Print slip — uses iframe (works on iPad/mobile, no popup blocker issues)
  const printViaIframe = (html) => {
    const existingFrame = document.getElementById('print-frame');
    if (existingFrame) existingFrame.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    iframe.onload = () => {
      try { iframe.contentWindow.print(); } catch { /* fallback handled below */ }
    };
    // Also trigger print after a short delay for Safari/iPad
    setTimeout(() => {
      try { iframe.contentWindow.print(); } catch {}
    }, 500);
  };

  // Build the "route" meta cells (Receiving From / Sending To / Issued To)
  // shared between fallback slip (generatePrintHTML) and live slip (handlePrint).
  //   t            : full transaction doc
  //   issuedToName : already-resolved label for issued_to (optional)
  const buildSlipRouteHTML = (t, issuedToName) => {
    const issuedTo = issuedToName || (t.issued_to ? (OPERATION_NAMES[t.issued_to] || t.issued_to) : "");
    // Case 1: Incoming Store + Outsource → received from supplier X, sent to supplier Y
    if (t.is_outsource && t.operation === 'incoming_store' && t.outsource_supplier) {
      const fromLabel = t.supplier ? `${t.supplier}${t.process ? ' — ' + (OPERATION_NAMES[t.process] || t.process) : ''}` : 'External';
      const toLabel = `${t.outsource_supplier}${t.outsource_process ? ' — ' + (OPERATION_NAMES[t.outsource_process] || t.outsource_process) : ''}`;
      return `<div class="meta-item" style="background:#FEF3C7;border-color:#FCD34D"><strong>Receiving From:</strong> ${fromLabel}</div>
        <div class="meta-item" style="background:#DCFCE7;border-color:#86EFAC"><strong>Sending To:</strong> ${toLabel}</div>`;
    }
    // Case 2: Semi-Firewall (or any in-house op) + Outsource → from op → supplier
    if (t.is_outsource && t.outsource_supplier) {
      const fromLabel = OPERATION_NAMES[t.operation] || t.operation;
      const toLabel = `${t.outsource_supplier}${t.outsource_process ? ' — ' + (OPERATION_NAMES[t.outsource_process] || t.outsource_process) : ''}`;
      return `<div class="meta-item" style="background:#FEF3C7;border-color:#FCD34D"><strong>Receiving From:</strong> ${fromLabel}</div>
        <div class="meta-item" style="background:#DCFCE7;border-color:#86EFAC"><strong>Sending To:</strong> ${toLabel}</div>`;
    }
    // Case 3: Incoming Store (in-house mode) → from supplier, issued to next op
    if (t.operation === 'incoming_store' && t.supplier) {
      const fromLabel = `${t.supplier}${t.process ? ' — ' + (OPERATION_NAMES[t.process] || t.process) : ''}`;
      const toLabel = issuedTo || 'N/A';
      return `<div class="meta-item" style="background:#FEF3C7;border-color:#FCD34D"><strong>Receiving From:</strong> ${fromLabel}</div>
        <div class="meta-item"><strong>Issued To:</strong> ${toLabel}</div>`;
    }
    // Case 4: Outsource sheet (receiving back from supplier)
    if (t.operation === 'outsource' && t.supplier) {
      const fromLabel = `${t.supplier}${t.process ? ' (' + (OPERATION_NAMES[t.process] || t.process) + ')' : ''}`;
      const toLabel = issuedTo || 'N/A';
      return `<div class="meta-item"><strong>Received From:</strong> ${fromLabel}</div>
        <div class="meta-item"><strong>Issued To:</strong> ${toLabel}</div>`;
    }
    // Default: just Issued To
    const fallback = issuedTo || (t.supplier ? t.supplier + (t.process ? ' (' + (OPERATION_NAMES[t.process] || t.process) + ')' : '') : 'N/A');
    return `<div class="meta-item"><strong>Issued To:</strong> ${fallback}</div>`;
  };

  const generatePrintHTML = (transData, partData) => {
    const t = transData;
    const opNameLocal = OPERATION_NAMES[t.operation] || t.operation;
    return `<!DOCTYPE html><html><head><title>Transaction Slip - ${t.data_id || ''}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:16px 20px 20px 12px;color:#333}
      .header{display:flex;align-items:center;gap:20px;border-bottom:3px solid #1D2951;padding-bottom:15px;margin-bottom:15px}
      .header img{width:320px;height:90px;object-fit:contain;mix-blend-mode:multiply;flex-shrink:0}
      .header-text{text-align:left}
      .header-text h1{margin:0;color:#1D2951;font-size:20px}
      .header-text p{margin:2px 0;color:#666;font-size:12px}
      .meta{display:flex;justify-content:space-between;margin-bottom:15px;font-size:13px;flex-wrap:wrap;gap:8px}
      .meta-item{background:#f8f9fa;padding:8px 12px;border-radius:4px;border:1px solid #e2e8f0}
      .meta-item strong{color:#1D2951}
      table{width:100%;border-collapse:collapse;margin-bottom:15px}
      th{background:#1D2951;color:white;padding:8px;text-align:left;font-size:11px}
      td{padding:8px;border:1px solid #ddd;font-size:12px}
      .flow{font-size:11px;color:#666;text-align:center;margin-top:15px;padding-top:10px;border-top:1px solid #ddd}
      .flow span{margin:0 3px}
      .flow .current{color:#1D2951;font-weight:bold;text-decoration:underline}
      @media print{body{margin:10px}}
    </style></head><body>
    <div class="header">
      <img src="${slipLogoUrl}" alt="Logo"/>
      <div class="header-text">
        <h1>${slipCompanyName}</h1>
        <p>Manufacturing ERP - Transaction Record</p>
      </div>
    </div>
    <div class="meta">
      <div class="meta-item"><strong>Process:</strong> ${opNameLocal}</div>
      <div class="meta-item"><strong>Data ID:</strong> ${t.data_id || 'N/A'}</div>
      <div class="meta-item"><strong>Date:</strong> ${t.date}</div>
      ${buildSlipRouteHTML(t)}
    </div>
    <table>
      <tr><th>Part No</th><td>${t.part_no}</td><th>Description</th><td>${partData?.description || ''}</td></tr>
      <tr><th>Gross Weight</th><td>${partData?.gross_weight || '-'} Kg</td><th>Finished Weight</th><td>${partData?.finished_weight || '-'} Kg</td></tr>
    </table>
    <table>
      <tr><th>Field</th><th>Value</th><th>Field</th><th>Value</th></tr>
      <tr><td><strong>Opening Balance</strong></td><td>${parseFloat(t.opening_balance || 0).toFixed(2)}${t.operation === 'cutting' ? ' Kg' : ' Nos'}</td>
          <td><strong>Received Quantity${t.operation === 'cutting' ? ' (Kg)' : ' (Nos)'}</strong></td><td>${parseFloat(t.operation === 'cutting' ? t.received_qty : t.received_qty_nos || 0).toFixed(2)}</td></tr>
      <tr><td><strong>Inspected Quantity (Nos)</strong></td><td>${parseFloat(t.produced_qty || 0).toFixed(2)}</td>
          <td><strong>OK Quantity (Nos)</strong></td><td>${parseFloat(t.ok_qty || 0).toFixed(2)}</td></tr>
      <tr><td><strong>Rejected Quantity (Nos)</strong></td><td>${parseFloat(t.rejected_qty || 0).toFixed(2)}</td>
          <td><strong>Rework Quantity (Nos)</strong></td><td>${parseFloat(t.rework_qty || 0).toFixed(2)}</td></tr>
      <tr><td><strong>Issued Quantity (Nos)</strong></td><td>${parseFloat(t.issued_qty || 0).toFixed(2)}</td>
          <td><strong>Balanced Quantity Produced</strong></td><td>${parseFloat(t.balanced_qty_produced || 0).toFixed(2)}</td></tr>
      <tr><td><strong>Balanced Quantity Issued (Nos)</strong></td><td>${parseFloat(t.balanced_qty_issued || 0).toFixed(2)}</td>
          <td><strong>Carry Forward</strong></td><td>${parseFloat(t.carry_forward_balance || 0).toFixed(2)}</td></tr>
    </table>
    ${t.rejection_reason_type ? `<table><tr><th>Rejection Reason</th><td>${t.rejection_reason_type}</td><th>Remarks</th><td>${t.rejection_remarks || '-'}</td></tr></table>` : ''}
    ${t.remarks ? `<table><tr><th style="width:120px">Process Remarks</th><td colspan="3">${(t.remarks + '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</td></tr></table>` : ''}
    ${(t.rejection_details && t.rejection_details.length > 0) ? `<h4 style="margin:10px 0 5px;font-size:13px;color:#EF4444">Rejection Details (${t.rejection_details.length})</h4><table><tr><th>Sr</th><th>Defect Type</th><th>Details</th><th>Reason (6M)</th><th>Action</th></tr>${t.rejection_details.map((rd,i) => `<tr><td>${i+1}</td><td>${rd.defect_type || rd.reason || '-'}</td><td>${rd.defect_details || '-'}</td><td>${rd.reason || '-'}</td><td>${rd.action || '-'}</td></tr>`).join('')}</table>` : ''}
    ${(t.rework_details && t.rework_details.length > 0) ? `<h4 style="margin:10px 0 5px;font-size:13px;color:#F59E0B">Rework Details (${t.rework_details.length})</h4><table><tr><th>Sr</th><th>Defect Type</th><th>Details</th><th>Reason (6M)</th><th>Action</th></tr>${t.rework_details.map((rd,i) => `<tr><td>${i+1}</td><td>${rd.defect_type || rd.reason || '-'}</td><td>${rd.defect_details || '-'}</td><td>${rd.reason || '-'}</td><td>${rd.action || '-'}</td></tr>`).join('')}</table>` : ''}
    <div class="flow"><strong>Operation Flow:</strong> ${OPERATION_ORDER_DISPLAY.map(o => o === opNameLocal ? `<span class="current">${o}</span>` : `<span>${o}</span>`).join(' → ')}</div>
    </body></html>`;
  };

  const openInvoiceDialog = async (t) => {
    try {
      const r = await axios.get(`${API}/transactions/${t.id}/invoice-info`);
      const d = r.data;
      setInvoiceForm({
        qty: String(d.available_qty || 0),
        customer: d.suggested_customer || "",
        customer_address: "",
        customer_gstin: "",
        rate: String(d.suggested_rate || 0),
        gst_percent: "18",
        notes: "",
        company_name: d.company?.name || "",
        company_address: d.company?.address || "",
        company_gstin: d.company?.gstin || "",
        company_phone: d.company?.phone || "",
        company_email: d.company?.email || "",
      });
      setInvoiceDialog({ trans: t, info: d, existing: d.existing_invoices || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open invoice");
    }
  };

  /** Re-open the print view for a previously generated invoice. Uses the
   *  shared iframe printer so it works on iPad/mobile and never trips
   *  popup blockers. */
  const reprintInvoice = (inv) => {
    try {
      const html = buildInvoiceHTML(inv);
      printViaIframe(html);
    } catch (e) {
      toast.error("Could not render invoice");
      console.error(e);
    }
  };

  const submitInvoice = async () => {
    if (!invoiceDialog) return;
    const qty = parseFloat(invoiceForm.qty);
    const rate = parseFloat(invoiceForm.rate) || 0;
    const gstPct = parseFloat(invoiceForm.gst_percent) || 0;
    if (!qty || qty <= 0) { toast.error("Enter print qty"); return; }
    if (qty > (invoiceDialog.info.available_qty + 0.001)) {
      toast.error(`Max available: ${invoiceDialog.info.available_qty}`); return;
    }
    try {
      const res = await axios.post(`${API}/transactions/${invoiceDialog.trans.id}/print-invoice`, {
        qty, rate, gst_percent: gstPct,
        customer: invoiceForm.customer,
        customer_address: invoiceForm.customer_address,
        customer_gstin: invoiceForm.customer_gstin,
        notes: invoiceForm.notes,
        company_overrides: {
          name: invoiceForm.company_name,
          address: invoiceForm.company_address,
          gstin: invoiceForm.company_gstin,
          phone: invoiceForm.company_phone,
          email: invoiceForm.company_email,
        },
      });
      const inv = res.data.invoice;
      toast.success(`Invoice ${res.data.invoice_no} created. Remaining available: ${res.data.remaining_available}`);
      // Open invoice print window
      printInvoiceWindow(inv);
      setInvoiceDialog(null);
      // Refresh transactions list so updated invoiced_qty/available shows up
      if (selectedPart) await fetchTransactions(selectedPart.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record invoice");
    }
  };

  const printInvoiceWindow = (inv) => {
    try {
      const html = buildInvoiceHTML(inv);
      printViaIframe(html);
    } catch (e) {
      toast.error("Could not render invoice");
      console.error(e);
    }
  };

  /** Build the printable invoice HTML. Shared between first-time print
   *  and re-print so layout stays consistent. */
  const buildInvoiceHTML = (inv) => {
    const c = inv.company || {};
    const num = (n) => parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `<!DOCTYPE html><html><head><title>Invoice ${inv.invoice_no}</title>
    <style>
      @page { size: A4; margin: 14mm }
      body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #0F172A; }
      .wrap { max-width: 760px; margin: 0 auto; }
      .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1D2951; padding-bottom: 14px; }
      .co h1 { margin: 0 0 4px; font-size: 22px; color: #1D2951; }
      .co .info { font-size: 11px; color: #475569; line-height: 1.5; max-width: 400px; }
      .meta { text-align: right; font-size: 11px; }
      .meta .num { font-size: 20px; font-weight: 800; color: #DC2626; letter-spacing: 1px; }
      .billto { display: flex; gap: 30px; margin: 20px 0; padding: 14px; background: #F8FAFC; border-radius: 6px; }
      .billto .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #64748B; margin-bottom: 4px; }
      .billto .v { font-size: 13px; color: #0F172A; font-weight: 600; }
      table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 12px; }
      th { background: #1D2951; color: #fff; text-align: left; padding: 9px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
      td { padding: 9px 10px; border-bottom: 1px solid #E2E8F0; }
      td.num, th.num { text-align: right; font-family: ui-monospace, monospace; }
      .summary { margin-top: 14px; margin-left: auto; width: 320px; font-size: 12px; }
      .summary .row { display: flex; justify-content: space-between; padding: 5px 12px; }
      .summary .total { background: #059669; color: #fff; font-weight: 800; font-size: 14px; padding: 9px 12px; margin-top: 6px; border-radius: 4px; }
      .notes { margin-top: 26px; padding: 12px; background: #FEF3C7; border-left: 3px solid #F59E0B; font-size: 11px; color: #78350F; border-radius: 0 4px 4px 0; }
      .footer { margin-top: 50px; display: flex; justify-content: space-between; font-size: 10px; color: #64748B; padding-top: 14px; border-top: 1px solid #E2E8F0; }
      button.printBtn { padding: 8px 18px; background: #1D2951; color: #fff; border: 0; border-radius: 4px; font-weight: 600; cursor: pointer; margin-bottom: 16px; }
      @media print { button.printBtn { display: none } }
    </style></head><body>
    <div class="wrap">
      <button class="printBtn" onclick="window.print()">Print / Save as PDF</button>
      <div class="head">
        <div class="co">
          <h1>${c.name || '<i style="color:#DC2626">Company name not set</i>'}</h1>
          <div class="info">
            ${c.address ? `${c.address}<br/>` : ''}
            ${c.gstin ? `GSTIN: <b>${c.gstin}</b><br/>` : ''}
            ${c.phone ? `Tel: ${c.phone}` : ''}${c.phone && c.email ? ' · ' : ''}${c.email ? c.email : ''}
          </div>
        </div>
        <div class="meta">
          <div style="font-size: 11px; color: #64748B; text-transform: uppercase; letter-spacing: 1px;">Tax Invoice</div>
          <div class="num">${inv.invoice_no}</div>
          <div>Date: <b>${inv.date}</b></div>
        </div>
      </div>
      <div class="billto">
        <div style="flex:1">
          <div class="lbl">Bill To</div>
          <div class="v">${inv.customer || '<i style="color:#DC2626">Customer name not provided</i>'}</div>
          ${inv.customer_address ? `<div style="font-size:11px;color:#475569;margin-top:4px">${inv.customer_address}</div>` : ''}
          ${inv.customer_gstin ? `<div style="font-size:11px;color:#475569;margin-top:2px">GSTIN: <b>${inv.customer_gstin}</b></div>` : ''}
        </div>
        <div>
          <div class="lbl">Reference</div>
          <div class="v" style="font-family: ui-monospace, monospace">${inv.transaction_data_id || '-'}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Description</th><th class="num">Quantity</th><th class="num">Rate</th><th class="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div style="font-weight:600">${inv.part_no}</div>
              ${inv.notes ? `<div style="font-size:11px;color:#64748B;margin-top:3px">${inv.notes}</div>` : ''}
            </td>
            <td class="num">${num(inv.qty)}</td>
            <td class="num">${num(inv.rate)}</td>
            <td class="num">${num(inv.base_amount)}</td>
          </tr>
        </tbody>
      </table>
      <div class="summary">
        <div class="row"><span>Subtotal</span><b>${num(inv.base_amount)}</b></div>
        <div class="row"><span>GST @ ${num(inv.gst_percent)}%</span><b>${num(inv.gst_amount)}</b></div>
        <div class="total" style="display:flex;justify-content:space-between"><span>TOTAL</span><span>₹ ${num(inv.total_amount)}</span></div>
      </div>
      ${inv.notes ? `<div class="notes"><b>Notes:</b> ${inv.notes}</div>` : ''}
      <div class="footer">
        <div>This is a system-generated invoice. Goods once sold will not be taken back.</div>
        <div>Authorised Signatory</div>
      </div>
    </div></body></html>`;
  };

  const handlePrint = async (transId) => {
    const localTrans = transactions.find(t => t.id === transId);
    try {
      const res = await axios.get(`${API}/transactions/print/${transId}`);
      const d = res.data;
      const t = d.transaction;
      const html = `<!DOCTYPE html><html><head><title>Transaction Slip - ${d.data_id}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:16px 20px 20px 12px;color:#333}
        .header{display:flex;align-items:center;gap:20px;border-bottom:3px solid #1D2951;padding-bottom:15px;margin-bottom:15px}
        .header img{width:320px;height:90px;object-fit:contain;mix-blend-mode:multiply;flex-shrink:0}
        .header-text{text-align:left}
        .header-text h1{margin:0;color:#1D2951;font-size:20px}
        .header-text p{margin:2px 0;color:#666;font-size:12px}
        .meta{display:flex;justify-content:space-between;margin-bottom:15px;font-size:13px;flex-wrap:wrap;gap:8px}
        .meta-item{background:#f8f9fa;padding:8px 12px;border-radius:4px;border:1px solid #e2e8f0}
        .meta-item strong{color:#1D2951}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#1D2951;color:white;padding:8px;text-align:left;font-size:11px}
        td{padding:8px;border:1px solid #ddd;font-size:12px}
        .flow{font-size:11px;color:#666;text-align:center;margin-top:15px;padding-top:10px;border-top:1px solid #ddd}
        .flow span{margin:0 3px}
        .flow .current{color:#1D2951;font-weight:bold;text-decoration:underline}
        @media print{body{margin:10px}}
      </style></head><body>
      <div class="header">
        <img src="${slipLogoUrl}" alt="Logo"/>
        <div class="header-text">
          <h1>${d.company_name || slipCompanyName}</h1>
          <p>Manufacturing ERP - Transaction Record</p>
        </div>
      </div>
      <div class="meta">
        <div class="meta-item"><strong>Process:</strong> ${d.operation_name}</div>
        <div class="meta-item"><strong>Data ID:</strong> ${d.data_id || 'N/A'}</div>
        <div class="meta-item"><strong>Date:</strong> ${t.date}</div>
        ${buildSlipRouteHTML(t, d.issued_to)}
      </div>
      <table>
        <tr><th>Part No</th><td>${t.part_no}</td><th>Description</th><td>${d.part?.description || ''}</td></tr>
        <tr><th>Gross Weight</th><td>${d.part?.gross_weight || '-'} Kg</td><th>Finished Weight</th><td>${d.part?.finished_weight || '-'} Kg</td></tr>
      </table>
      <table>
        <tr><th>Field</th><th>Value</th><th>Field</th><th>Value</th></tr>
        <tr><td><strong>Opening Balance</strong></td><td>${parseFloat(t.opening_balance || 0).toFixed(2)}${type === 'cutting' ? ' Kg' : ' Nos'}</td>
            <td><strong>Received Quantity${type === 'cutting' ? ' (Kg)' : ' (Nos)'}</strong></td><td>${parseFloat(type === 'cutting' ? t.received_qty : t.received_qty_nos || 0).toFixed(2)}</td></tr>
        <tr><td><strong>Inspected Quantity (Nos)</strong></td><td>${parseFloat(t.produced_qty || 0).toFixed(2)}</td>
            <td><strong>OK Quantity (Nos)</strong></td><td>${parseFloat(t.ok_qty || 0).toFixed(2)}</td></tr>
        <tr><td><strong>Rejected Quantity (Nos)</strong></td><td>${parseFloat(t.rejected_qty || 0).toFixed(2)}</td>
            <td><strong>Rework Quantity (Nos)</strong></td><td>${parseFloat(t.rework_qty || 0).toFixed(2)}</td></tr>
        <tr><td><strong>Issued Quantity (Nos)</strong></td><td>${parseFloat(t.issued_qty || 0).toFixed(2)}</td>
            <td><strong>Balanced Quantity Produced</strong></td><td>${parseFloat(t.balanced_qty_produced || 0).toFixed(2)}</td></tr>
        <tr><td><strong>${d.supplier_balance_label || 'Balanced Quantity Issued (Nos)'}</strong></td><td>${parseFloat(d.supplier_balance != null ? d.supplier_balance : (t.balanced_qty_issued || 0)).toFixed(2)}</td>
            <td><strong>Carry Forward</strong></td><td>${parseFloat(t.carry_forward_balance || 0).toFixed(2)}</td></tr>
      </table>
      ${t.rejection_reason_type ? `<table><tr><th>Rejection Reason</th><td>${t.rejection_reason_type}</td><th>Remarks</th><td>${t.rejection_remarks || '-'}</td></tr></table>` : ''}
      ${d.remarks || t.remarks ? `<table><tr><th style="width:120px">Process Remarks</th><td colspan="3">${((d.remarks || t.remarks) + '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</td></tr></table>` : ''}
      ${(d.rejection_details && d.rejection_details.length > 0) ? `<h4 style="margin:10px 0 5px;font-size:13px;color:#EF4444">Rejection Details (${d.rejection_details.length})</h4><table><tr><th>Sr</th><th>Reason</th><th>Remark</th></tr>${d.rejection_details.map((rd,i) => `<tr><td>${i+1}</td><td>${rd.reason || '-'}</td><td>${rd.remark || '-'}</td></tr>`).join('')}</table>` : ''}
      ${(d.rework_details && d.rework_details.length > 0) ? `<h4 style="margin:10px 0 5px;font-size:13px;color:#F59E0B">Rework Details (${d.rework_details.length})</h4><table><tr><th>Sr</th><th>Reason</th><th>Remark</th></tr>${d.rework_details.map((rd,i) => `<tr><td>${i+1}</td><td>${rd.reason || '-'}</td><td>${rd.remark || '-'}</td></tr>`).join('')}</table>` : ''}
      <div class="flow"><strong>Operation Flow:</strong> ${OPERATION_ORDER_DISPLAY.map(o => o === d.operation_name ? `<span class="current">${o}</span>` : `<span>${o}</span>`).join(' → ')}</div>
      </body></html>`;
      printViaIframe(html);
    } catch {
      // FALLBACK: Generate print from local transaction data
      if (localTrans) {
        printViaIframe(generatePrintHTML(localTrans, selectedPart));
        toast.info("Printed from local data (API unavailable)");
      } else {
        toast.error("No print data available");
      }
    }
  };

  const handlePrintReturnSlip = async (t) => {
    try {
      const returns = await axios.get(`${API}/transactions/return-history/${t.part_id}`);
      const relatedReturns = (returns.data || []).filter(r =>
        r.source_transaction_id === t.id || r.target_transaction_id === t.id
      );
      if (relatedReturns.length === 0) {
        toast.info("No return records found for this transaction");
        return;
      }
      const ret = relatedReturns[0]; // Most recent
      const html = `<!DOCTYPE html><html><head><title>Return Slip - ${ret.slip_id || 'RET'}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:16px 20px 20px 12px;color:#333}
        .header{display:flex;align-items:center;gap:20px;border-bottom:3px solid #7C3AED;padding-bottom:15px;margin-bottom:15px}
        .header img{width:320px;height:90px;object-fit:contain;mix-blend-mode:multiply}
        .header-text h1{margin:0;color:#7C3AED;font-size:20px}
        .header-text p{margin:2px 0;color:#666;font-size:12px}
        .info{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px}
        .info-item{background:#f5f3ff;padding:10px;border-radius:6px;border:1px solid #c4b5fd}
        .info-item strong{color:#5b21b6;display:block;font-size:11px;margin-bottom:2px}
        .info-item span{font-size:14px;font-family:monospace}
        .status{text-align:center;padding:12px;border-radius:6px;font-weight:bold;font-size:16px;margin-top:15px}
        .approved{background:#d1fae5;color:#059669;border:2px solid #059669}
        .pending{background:#fef3c7;color:#d97706;border:2px solid #d97706}
        .rejected{background:#fee2e2;color:#dc2626;border:2px solid #dc2626}
        @media print{body{margin:10px}}
      </style></head><body>
      <div class="header">
        <img src="${slipLogoUrl}" alt="Logo"/>
        <div class="header-text">
          <h1>${slipCompanyName} — Return To Stock Slip</h1>
          <p>Manufacturing ERP - Stock Return Record</p>
        </div>
      </div>
      <div class="info">
        <div class="info-item"><strong>Slip ID</strong><span>${ret.slip_id || ret.id?.slice(0,8)}</span></div>
        <div class="info-item"><strong>Date</strong><span>${ret.created_at ? new Date(ret.created_at).toLocaleDateString() : '-'}</span></div>
        <div class="info-item"><strong>Part No</strong><span>${ret.part_no}</span></div>
        <div class="info-item"><strong>Return Quantity</strong><span style="font-size:18px;font-weight:bold;color:#7C3AED">${ret.return_qty}</span></div>
        <div class="info-item"><strong>Return From (Source)</strong><span>${OPERATION_NAMES[ret.source_operation] || ret.source_operation} (${ret.source_data_id || '-'})</span></div>
        <div class="info-item"><strong>Return To (Destination)</strong><span>${OPERATION_NAMES[ret.target_operation] || ret.target_operation} (${ret.target_data_id || '-'})</span></div>
        <div class="info-item"><strong>Requested By</strong><span>${ret.created_by || '-'}</span></div>
        <div class="info-item"><strong>Approved By</strong><span>${ret.approved_by || '-'}</span></div>
      </div>
      <div class="status ${ret.status}">${(ret.status || 'pending').toUpperCase()}</div>
      </body></html>`;
      printViaIframe(html);
    } catch (e) {
      toast.error("Failed to load return data");
    }
  };

  const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  // ========== APPROVAL & RECEIPT ENTRIES SECTION ==========
  // ========== INCOMING ISSUES DISPLAY (replaces old receipt approval) ==========
  const renderIncomingIssuesSection = () => {
    if (!formData.part_id || incomingDetails.length === 0) return null;
    return (
      <div className="p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded space-y-2" data-testid="incoming-issues-section">
        <Label className="text-xs font-bold text-[#1D2951]">Incoming Issues (auto-received)</Label>
        <table className="w-full text-xs">
          <thead><tr className="bg-[#1D2951] text-white">
            <th className="px-2 py-1 text-left">Source</th>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-right">Quantity</th>
          </tr></thead>
          <tbody>
            {incomingDetails.map((d, i) => (
              <tr key={i} className="border-b border-[#BFDBFE] hover:bg-[#DBEAFE]">
                <td className="px-2 py-1">{d.from_op || "Unknown"}</td>
                <td className="px-2 py-1 font-mono">{d.date}</td>
                <td className="px-2 py-1 text-right font-mono font-bold">
                  {d.qty || d.qty_kg || 0} {d.source === "raw_material" ? "Kg" : isCutting ? "Kg" : "Nos"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-[#64748B]">
          Total auto-received: <b className="text-[#2563EB]">{incomingIssuesQty} {isCutting ? "Kg" : "Nos"}</b>
          {" "}(you can add more in the Total Received field above)
        </p>
      </div>
    );
  };

  // ========== OUTSOURCE LAYOUT — NEW: a(Opening), b(Received from semi-fw), c(PrevBalIssue), d(Issued), e(BalIssued), f(SupplierStock) ==========
  const renderOutsourceForm = () => {
    const a = parseFloat(formData.opening_balance) || 0;
    const b = parseFloat(formData.total_received_qty) || 0;
    const c = parseFloat(formData.issued_balance) || 0;
    const d = parseFloat(formData.issued_qty) || 0;
    const e = b + c - d;
    const f = a + d;
    const selectedSup = suppliers.find(s => s.name === outsourceSupplier);
    const supProcesses = selectedSup?.processes || [];
    return (
    <>
      {/* Supplier + Process selection lives in the form HEADER (above Part
          No / Date) — kept here only as a no-op marker; the original block
          was relocated to satisfy the new layout request. */}

      {/* Outsource received from semi-fw details */}
      {outsourceReceivedDetails.length > 0 && (
        <div className="p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded space-y-2">
          <Label className="text-xs font-bold text-[#1D2951]">Received from Semi-Firewall</Label>
          <table className="w-full text-xs">
            <thead><tr className="bg-[#1D2951] text-white">
              <th className="px-2 py-1 text-left">Data ID</th>
              <th className="px-2 py-1 text-left">Date</th>
              <th className="px-2 py-1 text-right">Quantity</th>
            </tr></thead>
            <tbody>
              {outsourceReceivedDetails.map((d, i) => (
                <tr key={i} className="border-b border-[#BFDBFE]">
                  <td className="px-2 py-1 font-mono">{d.data_id || "-"}</td>
                  <td className="px-2 py-1 font-mono">{d.date}</td>
                  <td className="px-2 py-1 text-right font-mono font-bold">{d.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Main form fields */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#F8FAFC] border rounded mt-3">
        <div>
          <Label className="text-xs font-semibold">(A) Opening Balance</Label>
          <Input type="number" value={f2(a)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="opening-balance-input" />
          <p className="text-xs text-[#64748B]">From Item Master / prev Supplier Stock</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">(B) Received Quantity</Label>
          <Input type="number" value={f2(b)} readOnly
            className="min-h-[44px] font-mono bg-[#EFF6FF] border-[#93C5FD]" data-testid="total-received-qty-input" />
          <p className="text-xs text-[#64748B]">Issued from Semi-Firewall</p>
        </div>
        <div>
          <Label className="text-xs font-semibold">(C) Previous Balance Issue</Label>
          <Input type="number" value={f2(c)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="issued-balance-display" />
          <p className="text-xs text-[#64748B]">Previous entry's Balance Issued</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#F8FAFC] border rounded mt-3">
        <div>
          <Label className="text-xs font-semibold text-[#059669]">(D) Issued Quantity *</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.issued_qty} onChange={e => setField("issued_qty", e.target.value)}
            className="min-h-[44px] font-mono border-[#059669]" data-testid="issued-qty-input" />
          <p className="text-xs text-[#64748B]">D &le; B + C ({(b + c).toFixed(2)})</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#7C3AED]">(E) Balance Issued</Label>
          <Input type="number" value={e.toFixed(2)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#7C3AED]" data-testid="balanced-issued-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(F) Supplier Stock</Label>
          <Input type="number" value={f.toFixed(2)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-lg text-[#1D2951]" data-testid="supplier-stock-display" />
        </div>
      </div>
    </>
    );
  };

  // ========== DISPATCH LAYOUT (Simplified) ==========
  const renderDispatchForm = () => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold">Opening Balance (Nos)</Label>
          <Input type="number" value={f2(formData.opening_balance)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="opening-balance-input" />
          <p className="text-xs text-[#64748B]">Auto from last transaction</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">Total Received Quantity (Nos)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.total_received_qty}
            onChange={e => onTotalReceivedChange(e.target.value)}
            className="min-h-[44px] font-mono border-[#93C5FD]"
            data-testid="total-received-qty-input" />
          <p className="text-xs text-[#64748B]">From issues + manual</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">Available Quantity (Nos)</Label>
          <Input type="number" value={f2(formData.total_stock_nos)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#1D2951]" data-testid="total-stock-nos-display" />
          <p className="text-xs text-[#64748B]">Opening + Received</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#059669]">Dispatched Quantity (Nos) *</Label>
          <Input type="number" value={f2(formData.issued_qty)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed border-[#059669]"
            data-testid="issued-qty-input" />
          <p className="text-xs text-[#64748B]">Auto = Σ Dispatch Quantity 2 (PO table below)</p>
        </div>
      </div>

      {renderIncomingIssuesSection()}

      <div className="grid grid-cols-1 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold text-[#7C3AED]">Balance Quantity (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_produced)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-lg" data-testid="balanced-produced-display" />
          <p className="text-xs text-[#64748B]">Available - Dispatched</p>
        </div>
      </div>

      {/* Open POs table for this part */}
      <DispatchPOTable
        ref={dispatchPORef}
        partId={selectedPart?.id}
        available={parseFloat(formData.total_stock_nos) || 0}
        editMode={!!editingTransId}
        onSumChange={(s) => {
          setFormData(prev => (parseFloat(prev.issued_qty) || 0) === s ? prev : { ...prev, issued_qty: s });
        }}
      />
    </>
  );

  // ========== FIREWALL LAYOUT ==========
  const renderFirewallForm = () => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold">(A) Opening Quantity (Nos)</Label>
          <Input type="number" value={f2(formData.opening_balance)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="opening-balance-input" />
          <p className="text-xs text-[#64748B]">Auto: Prev L</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">(B) Total Received Quantity (Nos)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.total_received_qty}
            onChange={e => onTotalReceivedChange(e.target.value)}
            className="min-h-[44px] font-mono border-[#93C5FD]"
            data-testid="total-received-qty-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#8B5CF6]">(K) Previous Cumulative Received</Label>
          <Input type="number" value={f2(prevCumulativeReceived)} readOnly
            className="min-h-[44px] font-mono bg-[#F5F3FF] cursor-not-allowed" data-testid="prev-cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#6366F1]">Cumulative Received</Label>
          <Input type="number" value={f2(prevCumulativeReceived + (parseFloat(formData.total_received_qty) || 0))} readOnly
            className="min-h-[44px] font-mono bg-[#EEF2FF] font-bold" data-testid="cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#059669]">Return Stock (Nos)</Label>
          <Input type="number" value={f2(formData.return_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#ECFDF5] border-[#A7F3D0] cursor-not-allowed font-bold" data-testid="return-stock-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#D97706]">Rework Stock (Nos)</Label>
          <Input type="number" value={f2(formData.rework_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed font-bold" data-testid="rework-stock-input" />
        </div>
      </div>

      {/* Return as it is */}
      <div className="flex items-center gap-3 p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded">
        <Checkbox checked={formData.return_as_is || false}
          onCheckedChange={v => setFormData(prev => ({ ...prev, return_as_is: !!v }))}
          data-testid="return-as-is-checkbox" />
        <div>
          <Label className="text-xs font-bold text-[#065F46]">Return as it is</Label>
          <p className="text-xs text-[#64748B]">Returned qty goes to Return Stock — does not affect Production or OK Quantity</p>
        </div>
      </div>

      <div className="grid grid-cols-1 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(D) Total Quantity (Nos)</Label>
          <Input type="number" value={f2(formData.total_qty)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="total-qty-display" />
        </div>
      </div>

      {renderIncomingIssuesSection()}

      <div className="border-t pt-4">
        <h4 className="text-sm font-bold text-[#1D2951] mb-3">Inspection (Nos)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-semibold">(E) Inspected Quantity (Nos)</Label>
            <Input type="number" step="any" min="0" max={MAX_VALUE}
              value={formData.produced_qty} onChange={e => setField("produced_qty", e.target.value)}
              className="min-h-[44px] font-mono" data-testid="produced-qty-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#EF4444]">(F) Rejected Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rejected_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FEF2F2] border-[#FCA5A5] cursor-not-allowed" data-testid="rejected-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#F59E0B]">(G) Rework Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rework_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed" data-testid="rework-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#10B981]">(H) OK Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.ok_qty)} readOnly className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="ok-qty-display" />
          </div>
        </div>
      </div>

      {renderDetailTables()}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#059669]">(K) Balance Issue (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_issued)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#059669]" data-testid="balanced-issued-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#7C3AED]">(L) Balance Production (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_produced)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#7C3AED]" data-testid="balanced-produced-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(M) Carry Forward (Nos)</Label>
          <Input type="number" value={f2(formData.carry_forward_balance)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-lg text-[#1D2951]" data-testid="carry-forward-display" />
        </div>
      </div>
    </>
  );

  // ========== CUTTING LAYOUT (kg) — with Total Received, K, Cumulative ==========
  const renderCuttingForm = () => {
    const gw = selectedPart?.gross_weight || 0;
    const totalRecv = parseFloat(formData.total_received_qty) || 0;
    const cumulativeRecv = prevCumulativeReceived + totalRecv;
    const recvNos = gw > 0 ? totalRecv / gw : 0;
    return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold">(A) Opening Balance (Kg)</Label>
          <Input type="number" value={f2(formData.opening_balance)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="opening-balance-input" />
          <p className="text-xs text-[#64748B]">Auto: Prev L x Gross Wt</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">(B) Total Received Quantity (Kg)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.total_received_qty} onChange={e => { setLastRecvEditField("kg"); onTotalReceivedChange(e.target.value); }}
            className="min-h-[44px] font-mono border-[#93C5FD]" data-testid="total-received-qty-input" />
          <p className="text-xs text-[#64748B]">Enter KG or Nos below</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">(B) Received Quantity (Nos)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.received_qty_nos}
            onChange={e => { setLastRecvEditField("nos"); setField("received_qty_nos", e.target.value); }}
            className="min-h-[44px] font-mono border-[#93C5FD]" data-testid="received-qty-nos-input" />
          <p className="text-xs text-[#64748B]">Nos = Kg / GW ({gw})</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#8B5CF6]">(K) Previous Cumulative Received</Label>
          <Input type="number" value={f2(prevCumulativeReceived)} readOnly
            className="min-h-[44px] font-mono bg-[#F5F3FF] cursor-not-allowed" data-testid="prev-cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#6366F1]">Cumulative Received (Kg)</Label>
          <Input type="number" value={f2(cumulativeRecv)} readOnly
            className="min-h-[44px] font-mono bg-[#EEF2FF] font-bold" data-testid="cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#059669]">Return Stock (Nos)</Label>
          <Input type="number" value={f2(formData.return_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#ECFDF5] border-[#A7F3D0] cursor-not-allowed font-bold" data-testid="return-stock-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#D97706]">Rework Stock (Nos)</Label>
          <Input type="number" value={f2(formData.rework_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed font-bold" data-testid="rework-stock-input" />
        </div>
      </div>
      <div className="grid grid-cols-1 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(D) Total Stock (Nos)</Label>
          <Input type="number" value={f2(formData.total_stock_nos)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="total-stock-nos-display" />
        </div>
      </div>

      {renderIncomingIssuesSection()}

      <div className="border-t pt-4">
        <h4 className="text-sm font-bold text-[#1D2951] mb-3">Production & Quality (Nos)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-semibold">(E) Produced Quantity (Nos) *</Label>
            <Input type="number" step="any" min="0" max={MAX_VALUE}
              value={formData.produced_qty} onChange={e => setField("produced_qty", e.target.value)}
              className="min-h-[44px] font-mono" data-testid="produced-qty-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#EF4444]">(F) Rejected Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rejected_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FEF2F2] border-[#FCA5A5] cursor-not-allowed" data-testid="rejected-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#F59E0B]">(G) Rework Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rework_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed" data-testid="rework-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#10B981]">(H) OK Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.ok_qty)} readOnly className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="ok-qty-display" />
          </div>
        </div>
      </div>

      {renderDetailTables()}

      <div className="border-t pt-4">
        <h4 className="text-sm font-bold text-[#1D2951] mb-3">Issue & Balance</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold">(I) Balance Issue Prev (Nos)</Label>
            <Input type="number" value={f2(formData.issued_balance)} readOnly
              className="min-h-[44px] bg-gray-100 font-mono" data-testid="issued-balance-display" />
          </div>
          <div>
            <Label className="text-xs font-semibold">(J) Issued Quantity (Nos) *</Label>
            <Input type="number" step="any" min="0" max={MAX_VALUE}
              value={formData.issued_qty} onChange={e => setField("issued_qty", e.target.value)}
              className="min-h-[44px] font-mono" data-testid="issued-qty-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#059669]">Balance Issue (Nos)</Label>
            <Input type="number" value={f2(formData.balanced_qty_issued)} readOnly
              className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#059669]" data-testid="balanced-issued-display" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#7C3AED]">(L) Balance Production (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_produced)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#7C3AED]" data-testid="balanced-produced-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(M) Carry Forward (Kg)</Label>
          <Input type="number" value={f2(formData.carry_forward_balance)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-lg text-[#1D2951]" data-testid="carry-forward-display" />
        </div>
      </div>

      {/* Return as it is — cutting flavour: material returns to RM Entry
          (not to a previous operation). Only usable AFTER save so we have
          a transaction_id to decrement. */}
      <div className="flex items-center justify-between p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded">
        <div>
          <Label className="text-xs font-bold text-[#065F46]">Return as it is</Label>
          <p className="text-xs text-[#64748B]">
            Return received Kg back to RM Entry (creates a negative-issue entry on the RM ledger).
          </p>
        </div>
        <Button type="button" onClick={handleReturnCuttingToRM} disabled={returnAsLoading}
          className="bg-[#059669] hover:bg-[#047857] text-white min-h-[38px] px-4"
          data-testid="return-cutting-to-rm-btn">
          <ArrowCounterClockwise size={16} className="mr-1" /> {returnAsLoading ? "Processing..." : "Return to RM"}
        </Button>
      </div>
    </>
    );
  };

  // ========== OTHER OPERATIONS (Nos only) — with Total Received, K, Cumulative ==========
  const renderStandardForm = () => {
    const totalRecv = parseFloat(formData.total_received_qty) || 0;
    const cumulativeRecv = prevCumulativeReceived + totalRecv;
    return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold">(A) Opening Balance (Nos)</Label>
          <Input type="number" value={f2(formData.opening_balance)} readOnly
            className="min-h-[44px] font-mono bg-gray-100 cursor-not-allowed" data-testid="opening-balance-input" />
          <p className="text-xs text-[#64748B]">Auto: Prev L</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#2563EB]">(B) Total Received Quantity (Nos)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.total_received_qty}
            onChange={e => onTotalReceivedChange(e.target.value)}
            className="min-h-[44px] font-mono border-[#93C5FD]"
            data-testid="total-received-qty-input" />
          <p className="text-xs text-[#64748B]">From issues + manual</p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#8B5CF6]">(K) Previous Cumulative Received</Label>
          <Input type="number" value={f2(prevCumulativeReceived)} readOnly
            className="min-h-[44px] font-mono bg-[#F5F3FF] cursor-not-allowed" data-testid="prev-cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#6366F1]">Cumulative Received (Nos)</Label>
          <Input type="number" value={f2(cumulativeRecv)} readOnly
            className="min-h-[44px] font-mono bg-[#EEF2FF] font-bold" data-testid="cumulative-recv" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#059669]">Return Stock (Nos)</Label>
          <Input type="number" value={f2(formData.return_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#ECFDF5] border-[#A7F3D0] cursor-not-allowed font-bold" data-testid="return-stock-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#D97706]">Rework Stock (Nos)</Label>
          <Input type="number" value={f2(formData.rework_stock)} readOnly
            className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed font-bold" data-testid="rework-stock-input" />
        </div>
      </div>

      {/* Return as it is — button to return received qty to source */}
      {!isCutting && (
        <div className="flex items-center justify-between p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded">
          <div>
            <Label className="text-xs font-bold text-[#065F46]">Return as it is</Label>
            <p className="text-xs text-[#64748B]">Return received qty to source operation's Return Stock</p>
          </div>
          <Button type="button" onClick={handleReturnAsIs} disabled={returnAsLoading}
            className="bg-[#059669] hover:bg-[#047857] text-white min-h-[38px] px-4"
            data-testid="return-as-is-btn">
            <ArrowCounterClockwise size={16} className="mr-1" /> {returnAsLoading ? "Processing..." : "Return"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(D) Total Stock (Nos)</Label>
          <Input type="number" value={f2(formData.total_stock_nos)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="total-stock-nos-display" />
        </div>
      </div>

      {renderIncomingIssuesSection()}

      <div className="border-t pt-4">
        <h4 className="text-sm font-bold text-[#1D2951] mb-3">{(isSemiFinish || isFirewall || isDispatch) ? 'Inspection' : 'Production'} (Nos)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-semibold">(E) {prodLabel} Quantity (Nos) *</Label>
            <Input type="number" step="any" min="0" max={MAX_VALUE}
              value={formData.produced_qty} onChange={e => setField("produced_qty", e.target.value)}
              className="min-h-[44px] font-mono" data-testid="produced-qty-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#EF4444]">(F) Rejected Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rejected_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FEF2F2] border-[#FCA5A5] cursor-not-allowed" data-testid="rejected-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#F59E0B]">(G) Rework Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.rework_qty)} readOnly
              className="min-h-[44px] font-mono bg-[#FFFBEB] border-[#FDE68A] cursor-not-allowed" data-testid="rework-qty-input" />
            <p className="text-xs text-[#64748B]">Sum from rows below</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#10B981]">(H) OK Quantity (Nos)</Label>
            <Input type="number" value={f2(formData.ok_qty)} readOnly className="min-h-[44px] bg-gray-100 font-mono font-bold" data-testid="ok-qty-display" />
          </div>
        </div>
      </div>

      {renderDetailTables()}

      {/* Semi-Firewall / Incoming Store: Outsource Checkbox */}
      {(isSemiFinish || isIncomingStore) && (
        <div className="flex items-center gap-2 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded">
          <Checkbox checked={outsourceChecked} onCheckedChange={v => setOutsourceChecked(!!v)} data-testid="outsource-checkbox" />
          <Label className="text-xs font-bold text-[#92400E]">Outsource (issue to external supplier instead of next operation)</Label>
        </div>
      )}

      {/* Editable Supplier/Function when outsource checked in edit mode */}
      {(isSemiFinish || isIncomingStore) && outsourceChecked && editingTransId && (
        <div className="grid grid-cols-2 gap-4 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded">
          <div>
            <Label className="text-xs font-semibold">Supplier</Label>
            <Select value={outsourceToSupplier} onValueChange={v => {
              setOutsourceToSupplier(v);
              const sup = suppliers.find(s => s.name === v);
              setOutsourceToProcess((sup?.processes || [])[0] || outsourceToProcess);
            }}>
              <SelectTrigger className="min-h-[44px]" data-testid="edit-outsource-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Function</Label>
            <Select value={outsourceToProcess} onValueChange={setOutsourceToProcess}>
              <SelectTrigger className="min-h-[44px]" data-testid="edit-outsource-process"><SelectValue placeholder="Function" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cutting">Cutting</SelectItem>
                <SelectItem value="machining">Machining</SelectItem>
                {(() => {
                  const sup = suppliers.find(s => s.name === outsourceToSupplier);
                  const procs = sup?.processes || [];
                  return (procs.length > 0 ? procs : outsourceProcessesList)
                    .map(p => <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>);
                })()}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Machining P1: Proceed Finishing to P2 */}
      {type === "machining_p1" && (
        <div className="flex items-center gap-2 p-3 bg-[#EDE9FE] border border-[#C4B5FD] rounded">
          <Checkbox checked={proceedToP2} onCheckedChange={v => setProceedToP2(!!v)} data-testid="proceed-to-p2-checkbox" />
          <Label className="text-xs font-bold text-[#5B21B6]">Proceed Finishing To Machining P2 (auto-transfer produced qty to P2)</Label>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#F8FAFC] border rounded">
        <div>
          <Label className="text-xs font-semibold">(I) Balance Issue Prev (Nos)</Label>
          <Input type="number" value={f2(formData.issued_balance)} readOnly className="min-h-[44px] bg-gray-100 font-mono" data-testid="issued-balance-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold">(J) Issued Quantity (Nos)</Label>
          <Input type="number" step="any" min="0" max={MAX_VALUE}
            value={formData.issued_qty} onChange={e => setField("issued_qty", e.target.value)}
            className="min-h-[44px] font-mono" data-testid="issued-qty-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#059669]">Balance Issue (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_issued)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#059669]" data-testid="balanced-issued-display" />
        </div>
      </div>

      {/* Editable "Issue To" in edit mode */}
      {editingTransId && !isDispatch && !isOutsource && (
        <div className="p-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded">
          <Label className="text-xs font-semibold text-[#0369A1] mb-1 block">Issue To Operation</Label>
          <Select value={issueToOperation} onValueChange={setIssueToOperation}>
            <SelectTrigger className="min-h-[44px]" data-testid="edit-issue-to-select">
              <SelectValue placeholder="Select target operation (leave empty if no issue)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">-- No Issue --</SelectItem>
              {liveOpFlow
                .filter(o => o.key !== type)
                .map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)
              }
            </SelectContent>
          </Select>
          <p className="text-xs text-[#64748B] mt-1">Change where the issued qty goes. Only applies if Issued Quantity &gt; 0.</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded">
        <div>
          <Label className="text-xs font-semibold text-[#7C3AED]">(L) Balance Production (Nos)</Label>
          <Input type="number" value={f2(formData.balanced_qty_produced)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-[#7C3AED]" data-testid="balanced-produced-display" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1D2951]">(M) Carry Forward (Nos)</Label>
          <Input type="number" value={f2(formData.carry_forward_balance)} readOnly
            className="min-h-[44px] bg-gray-100 font-mono font-bold text-lg text-[#1D2951]" data-testid="carry-forward-display" />
        </div>
        {isOutsource && (
          <div>
            <Label className="text-xs font-semibold">Supplier</Label>
            <Select value={formData.supplier} onValueChange={v => setField("supplier", v)}>
              <SelectTrigger className="min-h-[44px]" data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
    </>
    );
  };

  // ========== REJECTION/REWORK DETAIL TABLES ==========
  const renderDetailTables = () => {
    return (
      <div className="space-y-3">
        <DefectTable type="rejection" items={rejectionDetails} setItems={setRejectionDetails} />
        <DefectTable type="rework" items={reworkDetails} setItems={setReworkDetails} />
      </div>
    );
  };

  // History collapse state
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // ========== CUMULATIVE SUMMARY COMPUTATION ==========
  const computeCumulativeSummary = () => {
    if (transactions.length === 0) return null;
    // Sort oldest first for cumulative calc
    const sorted = [...transactions].reverse();
    const first = sorted[0];
    const latest = transactions[0]; // newest
    const gw = selectedPart?.gross_weight || parts.find(p => p.part_no === first.part_no)?.gross_weight || 0;

    // a = Opening Balance from first entry (Item Master value)
    const a = first.opening_balance || 0;
    // Cumulative sums
    let sumB = 0, sumC = 0, sumE = 0, sumF = 0, sumG = 0, sumJ = 0;
    for (const t of sorted) {
      sumB += (t.received_qty || 0);
      sumC += (t.received_qty_nos || 0);
      sumE += (t.produced_qty || 0);
      sumF += (t.rejected_qty || 0);
      sumG += (t.rework_qty || 0);
      sumJ += (t.issued_qty || 0);
    }
    // d = (a + sumB) / GW for cutting, a + sumC for others
    const d = isCutting && gw > 0 ? (a + sumB) / gw : (a + sumC);
    // h = sumE - sumF - sumG
    const h = Math.max(0, sumE - sumF - sumG);
    // i = last value of K (from latest transaction)
    const i = latest.balanced_qty_issued || 0;
    // k = h - sumJ
    const k = h - sumJ;
    // l = d - sumE
    const l = d - sumE;
    // m = (k + l) * GW for cutting, k + l for others
    const m = isCutting && gw > 0 ? (k + l) * gw : (k + l);

    return {
      id: 'summary', data_id: 'TOTAL', date: '-', part_no: first.part_no || '-',
      opening_balance: a,
      received_qty: sumB, received_qty_nos: sumC,
      total_stock_nos: d, total_qty: d,
      produced_qty: sumE,
      rejected_qty: sumF, rework_qty: sumG,
      ok_qty: h,
      issued_balance: i,
      issued_qty: sumJ,
      balanced_qty_issued: k,
      balanced_qty_produced: l,
      carry_forward_balance: m,
      _isSummary: true
    };
  };

  // ========== HISTORY TABLE (Collapsible, unified A-M structure) ==========
  const prodColLabel = isSemiFinish || isFirewall || isDispatch || isIncomingStore ? 'Inspection' : 'Production';
  const renderHistoryHeaders = () => {
    const common = <><th className="px-2 py-2 text-left text-xs font-bold">Data ID</th>
      <th className="px-2 py-2 text-left text-xs font-bold">Date</th>
      <th className="px-2 py-2 text-left text-xs font-bold">Part</th></>;
    const issuedToH = <th className="px-2 py-2 text-left text-xs font-bold text-[#6D28D9]">Issued To</th>;
    if (isDispatch) return (
      <tr>{common}
        <th className="px-2 py-2 text-right text-xs font-bold">Available</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Dispatched</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Balance</th>
        <th className="px-2 py-2 text-center text-xs font-bold">Actions</th>
      </tr>
    );
    if (isCutting) return (
      <tr>{common}
        <th className="px-2 py-2 text-right text-xs font-bold">Open Bal(Kg)</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Recd(Kg)</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Recd(Nos)</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#059669]">RetStk</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#D97706]">RwkStk</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Total(Nos)</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Produced</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Rej</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Rwk</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#10B981]">OK Quantity</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Issue Balance</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Issued</th>
        {issuedToH}
        <th className="px-2 py-2 text-right text-xs font-bold text-[#059669]">Balance Issue</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#7C3AED]">Balance Production</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#1D2951]">CF(Kg)</th>
        <th className="px-2 py-2 text-center text-xs font-bold">Act</th>
      </tr>
    );
    // Standard & Firewall — unified columns
    return (
      <tr>{common}
        <th className="px-2 py-2 text-right text-xs font-bold">Open Bal</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Recd(Nos)</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#059669]">RetStk</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#D97706]">RwkStk</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Total</th>
        <th className="px-2 py-2 text-right text-xs font-bold">{prodColLabel === 'Inspection' ? 'Inspected' : 'Produced'}</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Rej</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Rwk</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#10B981]">OK Quantity</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Issue Balance</th>
        <th className="px-2 py-2 text-right text-xs font-bold">Issued</th>
        {issuedToH}
        <th className="px-2 py-2 text-right text-xs font-bold text-[#059669]">Balance Issue</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#7C3AED]">Balance Production</th>
        <th className="px-2 py-2 text-right text-xs font-bold text-[#1D2951]">CF</th>
        <th className="px-2 py-2 text-center text-xs font-bold">Act</th>
      </tr>
    );
  };

  const ActionBtns = ({ t }) => (
    <div className="flex gap-0.5 justify-center">
      {isDispatch ? (
        <Button variant="ghost" size="sm" onClick={() => openInvoiceDialog(t)} className="h-7 w-7 p-0 text-[#059669]" title="Print Invoice" data-testid={`invoice-${t.data_id}`}><Printer size={14} /></Button>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setPrintChoiceDialog(t)} className="h-7 w-7 p-0 text-[#1D2951]" title="Print" data-testid={`print-${t.data_id}`}><Printer size={14} /></Button>
      )}
      {isAdmin && <Button variant="ghost" size="sm" onClick={() => handleEditTransaction(t)} className="h-7 w-7 p-0 text-[#3B82F6]" title="Edit" data-testid={`edit-${t.data_id}`}><PencilSimple size={14} /></Button>}
      {!isDispatch && <Button variant="ghost" size="sm" onClick={() => handleReturnToStock(t)} className="h-7 w-7 p-0 text-[#7C3AED]" title="Return To Stock" data-testid={`return-${t.data_id}`}><ArrowCounterClockwise size={14} /></Button>}
      {isAdmin && <Button variant="ghost" size="sm" onClick={() => setSmartDeleteTxnId(t.id)} className="h-7 w-7 p-0 text-[#EF4444]" title="Smart Delete" data-testid={`delete-${t.data_id}`}><Trash size={14} /></Button>}
    </div>
  );

  const renderHistoryRow = (t, idx, isSummaryRow = false) => {
    const isPlaceholder = !!t.is_rollover_placeholder;
    const rowBg = isSummaryRow
      ? 'bg-[#EFF6FF] font-bold'
      : (isPlaceholder ? 'bg-[#FEF3C7] hover:bg-[#FDE68A] italic text-[#92400E]' : 'hover:bg-[#F8FAFC]');
    const idCell = (
      <td className="px-2 py-2 text-xs font-mono font-semibold text-[#1D2951]"
          title={
            isPlaceholder
              ? 'Carry-forward from previous period — no transactions in current period yet'
              : (currentProc.has_remarks && t.remarks ? `Remarks: ${t.remarks}` : undefined)
          }>
        {isPlaceholder ? '↻ Carry-fwd' : (
          <span className="inline-flex items-center gap-1">
            {t.data_id || '-'}
            {currentProc.has_remarks && t.remarks && (
              <span className="text-[10px] text-[#7C3AED]" data-testid={`tx-remarks-indicator-${t.data_id}`}>📝</span>
            )}
          </span>
        )}
      </td>
    );
    const dateCell = <td className="px-2 py-2 text-xs font-mono">{t.date || (isPlaceholder ? '—' : '')}</td>;
    const partCell = <td className="px-2 py-2 text-xs font-semibold">{t.part_no}</td>;
    const c = (v, color) => <td className={`px-2 py-2 text-right text-xs font-mono ${color || ''}`}>{typeof v === 'number' ? v.toFixed(2) : (v ?? '0.00')}</td>;
    const cb = (v, color) => <td className={`px-2 py-2 text-right text-xs font-mono font-bold ${color || ''}`}>{typeof v === 'number' ? v.toFixed(2) : (v ?? '0.00')}</td>;
    // For Incoming Store + Outsource handoffs, the Balance Issue cell should
    // show the SOURCE supplier's remaining balance (from backend's
    // `supplier_balance`) instead of the part-level `balanced_qty_issued`.
    // Hover tooltip clarifies which supplier the balance is for.
    const balIssueCell = (() => {
      if (t.supplier_balance !== undefined && t.supplier_balance !== null) {
        return (
          <td className="px-2 py-2 text-right text-xs font-mono font-bold text-[#059669]"
              title={t.supplier_balance_label || ''}>
            {parseFloat(t.supplier_balance).toFixed(2)}
          </td>
        );
      }
      return cb(t.balanced_qty_issued, 'text-[#059669]');
    })();
    // For outsource transactions, "Issued To" should display the supplier
    // name (the actual outsource party) — not a generic operation label.
    // We fall back to `outsource_supplier` then `supplier` so legacy rows
    // still render meaningfully.
    const issuedToDisplay = (() => {
      const sup = t.outsource_supplier || t.supplier;
      if (t.is_outsource && sup) {
        const procLabel = t.process ? ` (${OPERATION_NAMES[t.process] || t.process})` : "";
        return `${sup}${procLabel}`;
      }
      if (isOutsource && sup) {
        const procLabel = t.process ? ` (${OPERATION_NAMES[t.process] || t.process})` : "";
        return `${sup}${procLabel}`;
      }
      return t.issued_to ? (OPERATION_NAMES[t.issued_to] || t.issued_to) : '-';
    })();
    const issuedToCell = <td className="px-2 py-2 text-xs font-semibold text-[#6D28D9]">{issuedToDisplay}</td>;
    const actionCell = isPlaceholder
      ? <td className="px-2 py-2 text-center text-[10px] text-[#92400E]" title="No actions on carry-forward">—</td>
      : <td className="px-2 py-2 text-center"><ActionBtns t={t} /></td>;

    if (isDispatch) {
      const available = (t.opening_balance ?? 0) + (t.received_qty_nos ?? 0);
      return (
        <tr key={t.id || idx} className={rowBg} data-testid={isSummaryRow ? 'summary-row' : (isPlaceholder ? `rollover-row-${idx}` : `transaction-row-${idx}`)}>
          {idCell}{dateCell}{partCell}
          {c(available)}{cb(t.issued_qty, 'text-[#059669]')}{cb(t.balanced_qty_produced, 'text-[#7C3AED]')}
          {!isSummaryRow && actionCell}
          {isSummaryRow && <td></td>}
        </tr>
      );
    }
    if (isCutting) return (
      <tr key={t.id || idx} className={rowBg} data-testid={isSummaryRow ? 'summary-row' : (isPlaceholder ? `rollover-row-${idx}` : `transaction-row-${idx}`)}>
        {idCell}{dateCell}{partCell}
        {c(t.opening_balance)}{c(t.received_qty)}{c(t.received_qty_nos)}
        {c(t.return_stock, 'text-[#059669]')}{c(t.rework_stock, 'text-[#D97706]')}
        {c(t.total_stock_nos)}{c(t.produced_qty)}
        {c(t.rejected_qty, 'text-[#EF4444]')}{c(t.rework_qty, 'text-[#F59E0B]')}
        {cb(t.ok_qty, 'text-[#10B981]')}{c(t.issued_balance)}{c(t.issued_qty)}
        {issuedToCell}
        {balIssueCell}{cb(t.balanced_qty_produced, 'text-[#7C3AED]')}
        {cb(t.carry_forward_balance, 'text-[#1D2951]')}
        {!isSummaryRow && actionCell}
        {isSummaryRow && <td></td>}
      </tr>
    );
    // Standard & Firewall — unified
    return (
      <tr key={t.id || idx} className={rowBg} data-testid={isSummaryRow ? 'summary-row' : (isPlaceholder ? `rollover-row-${idx}` : `transaction-row-${idx}`)}>
        {idCell}{dateCell}{partCell}
        {c(t.opening_balance)}{c(t.received_qty_nos)}
        {c(t.return_stock, 'text-[#059669]')}{c(t.rework_stock, 'text-[#D97706]')}
        {c(t.total_stock_nos || t.total_qty)}{c(t.produced_qty)}
        {c(t.rejected_qty, 'text-[#EF4444]')}{c(t.rework_qty, 'text-[#F59E0B]')}
        {cb(t.ok_qty, 'text-[#10B981]')}{c(t.issued_balance)}
        {c(t.issued_qty)}
        {issuedToCell}
        {balIssueCell}{cb(t.balanced_qty_produced, 'text-[#7C3AED]')}
        {cb(t.carry_forward_balance, 'text-[#1D2951]')}
        {!isSummaryRow && actionCell}
        {isSummaryRow && <td></td>}
      </tr>
    );
  };

  // ========== RETURN AS IT IS HANDLER ==========
  const handleReturnAsIs = async () => {
    if (!editingTransId && !formData.part_id) {
      toast.error("Select a part and save a transaction first");
      return;
    }
    const transId = editingTransId || lastSavedTransId;
    if (!transId) {
      toast.error("Save the transaction first, then use Return");
      return;
    }
    const received = parseFloat(formData.total_received_qty) || 0;
    if (received <= 0) { toast.error("No received qty to return"); return; }
    // Always show dialog — user picks where to return
    setReturnAsSources([]);
    setReturnAsSelections({ target_op: "", qty: received });
    setReturnAsIsDialog(true);
  };

  const handleReturnAsIsConfirm = async () => {
    const transId = editingTransId || lastSavedTransId;
    const targetOp = returnAsSelections.target_op;
    const qty = parseFloat(returnAsSelections.qty) || 0;
    if (!targetOp) { toast.error("Select an operation to return to"); return; }
    if (qty <= 0) { toast.error("Enter qty to return"); return; }
    setReturnAsLoading(true);
    try {
      await axios.post(`${API}/transactions/return-as-is`, {
        transaction_id: transId,
        returns: [{ source_operation: targetOp, qty }]
      });
      toast.success(`Returned ${qty} to ${OPERATION_NAMES[targetOp] || targetOp} — Return Stock updated`);
      setReturnAsIsDialog(false);
      fetchTransactions(formData.part_id);
      // Refresh form's return_stock from backend
      if (formData.part_id) {
        try {
          const res = await axios.get(`${API}/transactions/opening-balance/${formData.part_id}/${type}`);
          setFormData(prev => ({
            ...prev,
            return_stock: res.data.return_stock || 0,
            rework_stock: res.data.rework_stock || 0
          }));
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Return failed");
    }
    setReturnAsLoading(false);
  };

  // Cutting-specific return: material goes back to the RM Entry ledger
  // instead of to a source operation (cutting has no upstream op).
  const handleReturnCuttingToRM = async () => {
    const transId = editingTransId || lastSavedTransId;
    if (!transId) {
      toast.error("Save the cutting entry first, then use Return to RM");
      return;
    }
    const received = parseFloat(formData.received_qty) || 0;
    if (received <= 0) { toast.error("No received qty to return"); return; }
    const raw = window.prompt(
      `Return how many Kg back to RM Entry? (received: ${received} Kg)`,
      received
    );
    if (raw == null) return;
    const qty = parseFloat(raw);
    if (isNaN(qty) || qty <= 0) return toast.error("Enter a valid quantity");
    if (qty > received + 0.001) return toast.error(`Cannot return more than ${received} Kg`);
    setReturnAsLoading(true);
    try {
      const r = await axios.post(`${API}/transactions/return-cutting-to-rm`, {
        transaction_id: transId, qty_kg: qty,
      });
      toast.success(r.data.message);
      fetchTransactions(formData.part_id);
      // Bump the current form's received_qty to match the new server state.
      setFormData(prev => ({ ...prev, received_qty: r.data.new_received_qty }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Return failed");
    }
    setReturnAsLoading(false);
  };

  // Track last saved transaction ID for return/rework features
  const [lastSavedTransId, setLastSavedTransId] = useState(null);

  const handleExportAllHistory = async () => {
    try {
      toast.info("Exporting all transaction history...");
      const res = await axios.get(`${API}/transactions/export-all`);
      const allTrans = res.data;
      if (!allTrans || allTrans.length === 0) { toast.error("No transactions to export"); return; }
      const opGroups = {};
      const opOrder = ["cutting","incoming_store","machining_p1","machining_p2","semi_firewall","painting","silver_plating","welding","outsource","firewall","dispatch"];
      for (const t of allTrans) {
        const op = t.operation || "unknown";
        if (!opGroups[op]) opGroups[op] = [];
        opGroups[op].push({
          "Data ID": t.data_id || "",
          "Date": t.date || "",
          "Part No": t.part_no || "",
          "Opening Bal": t.opening_balance ?? 0,
          "Received (Nos)": t.received_qty_nos ?? t.received_qty ?? 0,
          "Produced": t.produced_qty ?? 0,
          "Rejected": t.rejected_qty ?? 0,
          "Rework": t.rework_qty ?? 0,
          "OK Quantity": t.ok_qty ?? 0,
          "Issued Quantity": t.issued_qty ?? 0,
          "Issued To": t.issued_to ? (OPERATION_NAMES[t.issued_to] || t.issued_to) : "",
          "Balance Issued": t.balanced_qty_issued ?? 0,
          "Balance Produced": t.balanced_qty_produced ?? 0,
          "Carry Fwd": t.carry_forward_balance ?? 0,
        });
      }
      const sheets = [];
      for (const op of opOrder) {
        if (opGroups[op] && opGroups[op].length > 0) {
          sheets.push({ name: OPERATION_NAMES[op] || op, data: opGroups[op] });
        }
      }
      // Add any remaining ops not in the standard list
      for (const op of Object.keys(opGroups)) {
        if (!opOrder.includes(op)) {
          sheets.push({ name: OPERATION_NAMES[op] || op, data: opGroups[op] });
        }
      }
      exportWithHeader(sheets, `All_Transaction_History_${new Date().toISOString().slice(0,10)}`, "All Operations — Transaction History");
      toast.success("Exported successfully!");
    } catch { toast.error("Export failed"); }
  };

  return (
    <OperationGuard key={type} operationName={opName} operationType={type}>
      <Layout>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{fontFamily: 'Chivo'}}>{opName}</h1>
              <p className="text-sm text-[#64748B] mt-1">
                {isFirewall ? "Quality inspection before dispatch" : isDispatch ? "Record dispatch to customer" : `Record ${(isSemiFinish || isFirewall || isDispatch) ? 'inspection' : 'production'} for ${opName.toLowerCase()}`}
              </p>
            </div>
            <button onClick={() => { setNotifOpen(true); setSelectedNotifs(new Set()); }} className="relative p-2.5 rounded-full hover:bg-[#F1F5F9] transition-colors" data-testid="notification-bell">
              <BellRinging size={24} weight={incomingCount > 0 ? "fill" : "regular"} className={incomingCount > 0 ? "text-[#F59E0B]" : "text-[#64748B]"} />
              {(() => {
                const visibleCount = incomingAll.filter(it => !dismissedNotifIds.has(it.id)).length;
                return visibleCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-count">
                    {visibleCount}
                  </span>
                );
              })()}
            </button>
          </div>

          <Card className="p-5 border border-[#E2E8F0]" data-testid="operation-form-card">
            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-4">
              {/* OUTSOURCE: supplier + process header sits ABOVE Part No / Date
                  per user preference (operator picks "who & what process"
                  first, then enters the part-specific data). */}
              {isOutsource && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-[#F1F5F9] border border-[#CBD5E1] rounded" data-testid="outsource-header-row">
                  <div>
                    <Label className="text-xs font-semibold">Supplier *</Label>
                    <Select value={outsourceSupplier} onValueChange={v => {
                      setOutsourceSupplier(v);
                      const sup = suppliers.find(s => s.name === v);
                      const proc = (sup?.processes || [])[0] || "";
                      setOutsourceProcess(proc);
                      setField("supplier", v);
                      setField("process", proc);
                      if (formData.part_id) fetchOutsourceData(formData.part_id, v, proc);
                    }}>
                      <SelectTrigger className="min-h-[44px]" data-testid="outsource-header-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                      <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Process *</Label>
                    <Select value={outsourceProcess} onValueChange={v => {
                      setOutsourceProcess(v);
                      setField("process", v);
                      if (formData.part_id && outsourceSupplier) fetchOutsourceData(formData.part_id, outsourceSupplier, v);
                    }}>
                      <SelectTrigger className="min-h-[44px]" data-testid="outsource-header-process"><SelectValue placeholder="Select process" /></SelectTrigger>
                      <SelectContent>
                        {(suppliers.find(s => s.name === outsourceSupplier)?.processes?.length
                          ? suppliers.find(s => s.name === outsourceSupplier).processes
                          : outsourceProcessesList || []).map(p => (
                          <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Part No *</Label>
                  <Popover open={partSearchOpen} onOpenChange={setPartSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={partSearchOpen}
                        className="w-full min-h-[44px] justify-between text-left font-normal" data-testid="part-select">
                        {formData.part_id ? (parts.find(p => p.id === formData.part_id)?.part_no + ' - ' + (parts.find(p => p.id === formData.part_id)?.description || '')) : "Search / Select part..."}
                        <MagnifyingGlass size={16} className="ml-2 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command filter={(value, search) => {
                        const part = parts.find(p => p.id === value);
                        if (!part) return 0;
                        const haystack = `${part.part_no} ${part.description || ''}`.toLowerCase();
                        const needle = search.toLowerCase();
                        return haystack.includes(needle) ? 1 : 0;
                      }}>
                        <CommandInput placeholder="Type part no to search..." data-testid="part-search-input" />
                        <CommandList>
                          <CommandEmpty>No part found.</CommandEmpty>
                          <CommandGroup>
                            {parts.filter(p => isAssembly ? p.part_type === "assembly" : p.part_type !== "assembly").map(p => (
                              <CommandItem key={p.id} value={p.id} onSelect={(val) => {
                                handlePartChange(val);
                                setPartSearchOpen(false);
                              }} className="cursor-pointer">
                                <span className="font-mono font-bold text-[#1D2951]">{p.part_no}</span>
                                <span className="ml-2 text-xs text-[#64748B]">{p.description}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Date *</Label>
                  <Input type="date" value={formData.date} onChange={e => setField("date", e.target.value)} required className="min-h-[44px]" data-testid="date-input" />
                </div>
              </div>

              {/* Assembly — BOM availability preview */}
              {isAssembly && bomAvailability && bomAvailability.lines && bomAvailability.lines.length > 0 && (
                <div className="mb-4 p-3 bg-[#F5F3FF] border-2 border-[#DDD6FE] rounded-lg" data-testid="bom-availability-card">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-[#6D28D9]">
                      Components Needed (for {bomAvailability.qty} assemblies)
                    </h4>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                      bomAvailability.all_sufficient ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"
                    }`}>
                      {bomAvailability.all_sufficient ? "✓ All sufficient" : "✗ Shortage detected"}
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-[#EDE9FE] text-[#6D28D9]">
                      <tr>
                        <th className="px-2 py-1 text-left font-bold">Component</th>
                        <th className="px-2 py-1 text-right font-bold">Quantity / Assembly</th>
                        <th className="px-2 py-1 text-right font-bold">Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomAvailability.lines.map((l, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]" data-testid={`bom-line-${i}`}>
                          <td className="px-2 py-1 font-mono font-semibold text-[#1D2951]">
                            {l.component_part_no} <span className="text-[#94A3B8] font-normal">{l.component_description ? `· ${l.component_description}` : ""}</span>
                          </td>
                          <td className="px-2 py-1 text-right font-mono">{l.qty_per_assembly}</td>
                          <td className={`px-2 py-1 text-right font-mono font-bold ${l.component_type === "bought_out" ? "text-[#7C3AED]" : (l.sufficient ? "text-[#15803D]" : "text-[#B91C1C]")}`}
                              title={l.component_type === "bought_out" ? "Bought-out — no stock tracking" : (l.sufficient ? "Sufficient" : `Short by ${l.shortage}`)}>
                            {l.component_type === "bought_out" ? (
                              <span className="text-[10px] font-normal uppercase tracking-wide">Bought Out</span>
                            ) : (
                              <>
                                {l.available_qty}
                                {!l.sufficient && <span className="ml-1 text-[10px] font-normal">(short {l.shortage})</span>}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isAssembly && selectedPart && (!bomAvailability || !bomAvailability.lines?.length) && (
                <div className="mb-4 p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded text-[11px] text-[#92400E]">
                  This assembly part has no BOM defined. Open Item Master → Assembly tab and add components.
                </div>
              )}

              {/* Optional Remarks field — only when process has has_remarks=true in Settings → Processes */}
              {currentProc.has_remarks && (
                <div className="mb-3">
                  <Label className="text-xs font-semibold">Remarks <span className="text-[#94A3B8] font-normal">(optional)</span></Label>
                  <Input
                    value={formData.remarks || ""}
                    onChange={e => setField("remarks", e.target.value)}
                    placeholder="Any note for this entry — visible in Transaction History tooltip and on the Issue Slip"
                    className="min-h-[44px]"
                    data-testid="remarks-input"
                  />
                </div>
              )}

              {isIncomingStore && (
                <div className="flex gap-2 mb-3">
                  <Button type="button" variant={incomingMode === "in_house" ? "default" : "outline"}
                    className={`flex-1 min-h-[40px] ${incomingMode === "in_house" ? "bg-[#1D2951] text-white" : ""}`}
                    onClick={() => setIncomingMode("in_house")} data-testid="incoming-inhouse-btn">
                    In-House
                  </Button>
                  <Button type="button" variant={incomingMode === "outsource" ? "default" : "outline"}
                    className={`flex-1 min-h-[40px] ${incomingMode === "outsource" ? "bg-[#059669] text-white" : ""}`}
                    onClick={() => setIncomingMode("outsource")} data-testid="incoming-outsource-btn">
                    Outsource
                  </Button>
                </div>
              )}

              {isIncomingStore && incomingMode === "outsource" && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-[#ECFDF5] border border-[#059669] rounded mb-3">
                  <div>
                    <Label className="text-xs font-semibold">Supplier</Label>
                    <Select value={formData.supplier} onValueChange={v => setField("supplier", v)}>
                      <SelectTrigger className="min-h-[44px]" data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                      <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Process</Label>
                    <Select value={formData.process} onValueChange={v => setField("process", v)}>
                      <SelectTrigger className="min-h-[44px]" data-testid="incoming-process-select"><SelectValue placeholder="Select process" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cutting">Cutting</SelectItem>
                        <SelectItem value="machining">Machining</SelectItem>
                        {outsourceProcessesList.map(sp => <SelectItem key={sp} value={sp}>{OPERATION_NAMES[sp] || sp.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {isDispatch ? renderDispatchForm() : isOutsource ? renderOutsourceForm() : isCutting ? renderCuttingForm() : renderStandardForm()}

              <div className="flex gap-3">
                {editingTransId && (
                  <div className="w-full mb-2 p-2 bg-[#FEF3C7] border border-[#F59E0B] rounded flex items-center justify-between">
                    <span className="text-xs font-bold text-[#92400E]">EDIT MODE — Modifying existing transaction</span>
                    <Button type="button" variant="outline" size="sm" onClick={cancelEdit}
                      className="h-7 text-xs text-[#DC2626] border-[#DC2626]" data-testid="cancel-edit-btn">
                      <X size={14} className="mr-1" /> Cancel Edit
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={!formData.part_id}
                  className={`flex-1 min-h-[44px] ${editingTransId ? 'bg-[#F59E0B] hover:bg-[#D97706]' : 'bg-[#1D2951] hover:bg-[#2A3B73]'} text-white`} data-testid="submit-transaction-button">
                  <CheckCircle size={18} className="mr-2" /> {editingTransId ? "Update Sheet" : "Save Transaction"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setFormData(emptyForm); setSelectedPart(null); setIsApproved(false); setEditingTransId(null); }}
                  className="min-h-[44px]" data-testid="reset-form-button">Reset</Button>
              </div>
            </form>
          </Card>

          {/* Rejection/Rework Dialog */}
          <Dialog open={!!reasonDialog} onOpenChange={() => setReasonDialog(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-lg font-bold text-[#1D2951]">Rejection / Rework Reason Required</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {reasonDialog?.needRejection && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-[#EF4444]">Rejection Reason *</Label>
                    <Select value={formData.rejection_reason_type} onValueChange={v => setField("rejection_reason_type", v)}>
                      <SelectTrigger className="min-h-[44px]" data-testid="rejection-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                      <SelectContent>{REJECTION_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Textarea value={formData.rejection_remarks} onChange={e => setField("rejection_remarks", e.target.value)}
                      placeholder="Optional remarks" className="min-h-[60px]" data-testid="rejection-remarks-input" />
                  </div>
                )}
                {reasonDialog?.needRework && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-[#F59E0B]">Rework Reason *</Label>
                    <Select value={formData.rework_reason_type} onValueChange={v => setField("rework_reason_type", v)}>
                      <SelectTrigger className="min-h-[44px]" data-testid="rework-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                      <SelectContent>{REJECTION_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Textarea value={formData.rework_remarks} onChange={e => setField("rework_remarks", e.target.value)}
                      placeholder="Optional remarks" className="min-h-[60px]" data-testid="rework-remarks-input" />
                  </div>
                )}
                <Button onClick={handleReasonSubmit}
                  disabled={(reasonDialog?.needRejection && !formData.rejection_reason_type) || (reasonDialog?.needRework && !formData.rework_reason_type)}
                  className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]" data-testid="submit-reason-button">
                  Submit with Reasons
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Print Choice Dialog */}
          {printChoiceDialog && (
            <Dialog open={!!printChoiceDialog} onOpenChange={() => setPrintChoiceDialog(null)}>
              <DialogContent className="max-w-xs" data-testid="print-choice-dialog">
                <DialogHeader><DialogTitle className="text-lg font-bold text-[#1D2951]">Print Options</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  <p className="text-xs text-[#64748B]">Transaction: <span className="font-bold">{printChoiceDialog.data_id}</span></p>
                  <Button onClick={() => { handlePrint(printChoiceDialog.id); setPrintChoiceDialog(null); }}
                    className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]" data-testid="print-issue-slip-btn">
                    <Printer size={16} className="mr-2" /> Issue Slip
                  </Button>
                  <Button onClick={() => { handlePrintReturnSlip(printChoiceDialog); setPrintChoiceDialog(null); }}
                    variant="outline" className="w-full min-h-[44px] text-[#7C3AED] border-[#7C3AED]" data-testid="print-return-slip-btn">
                    <ArrowCounterClockwise size={16} className="mr-2" /> Return To Stock Slip
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Print Invoice Dialog (Dispatch only) */}
          {invoiceDialog && (
            <Dialog open={!!invoiceDialog} onOpenChange={() => setInvoiceDialog(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold text-[#059669]">Print Invoice — {invoiceDialog.info.invoice_no_preview}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Stock summary banner */}
                  <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded p-3 grid grid-cols-3 gap-3 text-xs">
                    <div><div className="text-[#16803D] font-semibold">Inspected</div><div className="text-lg font-bold font-mono">{invoiceDialog.info.inspected_qty}</div></div>
                    <div><div className="text-[#16803D] font-semibold">Already Invoiced</div><div className="text-lg font-bold font-mono">{invoiceDialog.info.invoiced_qty}</div></div>
                    <div><div className="text-[#059669] font-semibold">Available</div><div className="text-lg font-bold font-mono text-[#059669]">{invoiceDialog.info.available_qty}</div></div>
                  </div>

                  {/* Existing invoices — let the user re-print previously
                       generated invoices for this transaction instead of
                       being forced to create a new one every time. */}
                  {(invoiceDialog.existing || []).length > 0 && (
                    <div className="border border-[#3B82F6] rounded p-3 bg-[#EFF6FF]" data-testid="existing-invoices-section">
                      <Label className="text-xs font-bold text-[#1E3A8A] mb-2 block">
                        Existing Invoices ({invoiceDialog.existing.length})
                      </Label>
                      <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                        {invoiceDialog.existing.map(inv => (
                          <div key={inv.id} className="flex items-center justify-between bg-white border border-[#BFDBFE] rounded px-2 py-1.5 text-xs"
                               data-testid={`existing-invoice-${inv.invoice_no}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-mono font-bold text-[#1E3A8A]">{inv.invoice_no}</div>
                              <div className="text-[10px] text-[#64748B]">
                                {inv.date} · Quantity {inv.qty} · ₹{inv.total_amount?.toFixed?.(2) || inv.total_amount}
                                {inv.customer ? ` · ${inv.customer}` : ''}
                              </div>
                            </div>
                            <Button size="sm" variant="outline"
                              onClick={() => reprintInvoice(inv)}
                              className="h-7 px-2 text-[11px] text-[#1E3A8A] border-[#3B82F6]"
                              data-testid={`reprint-${inv.invoice_no}`}>
                              <Printer size={12} className="mr-1" /> Re-print
                            </Button>
                          </div>
                        ))}
                      </div>
                      {invoiceDialog.info.available_qty > 0 && (
                        <p className="text-[10px] text-[#1E40AF] mt-2 italic">
                          {invoiceDialog.info.available_qty} units still available for a new invoice.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Print Quantity */}
                  <div>
                    <Label className="text-xs font-bold flex items-center justify-between">
                      <span>Print Invoice for Quantity *</span>
                      <span className="text-[10px] font-normal text-[#64748B]">Max: {invoiceDialog.info.available_qty}</span>
                    </Label>
                    <Input type="number" step="any" min="0" max={invoiceDialog.info.available_qty}
                      value={invoiceForm.qty}
                      onChange={e => setInvoiceForm(f => ({...f, qty: e.target.value}))}
                      className="min-h-[44px] font-mono text-lg" data-testid="invoice-qty-input" autoFocus />
                    {invoiceForm.qty && parseFloat(invoiceForm.qty) < invoiceDialog.info.available_qty && (
                      <p className="text-[11px] text-[#92400E] mt-1">
                        ↻ {(invoiceDialog.info.available_qty - parseFloat(invoiceForm.qty)).toFixed(2)} units will remain available for future invoices.
                      </p>
                    )}
                  </div>

                  {/* Customer details */}
                  <div className="border border-[#E2E8F0] rounded p-3">
                    <Label className="text-xs font-bold text-[#1D2951] mb-2 block">Bill To (Customer)</Label>
                    <div className="space-y-2">
                      <CustomerSelect value={invoiceForm.customer}
                        onChange={(v) => setInvoiceForm(f => ({...f, customer: v}))}
                        placeholder="Customer Name (leave blank if unknown)"
                        testId="invoice-customer-input" />
                      <Input placeholder="Customer Address" value={invoiceForm.customer_address}
                        onChange={e => setInvoiceForm(f => ({...f, customer_address: e.target.value}))}
                        className="min-h-[36px] text-sm" data-testid="invoice-customer-address-input" />
                      <Input placeholder="Customer GSTIN" value={invoiceForm.customer_gstin}
                        onChange={e => setInvoiceForm(f => ({...f, customer_gstin: e.target.value}))}
                        className="min-h-[36px] text-sm font-mono" data-testid="invoice-customer-gstin-input" />
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Rate (₹)</Label>
                      <Input type="number" step="any" min="0" value={invoiceForm.rate}
                        onChange={e => setInvoiceForm(f => ({...f, rate: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="invoice-rate-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">GST %</Label>
                      <Input type="number" step="any" min="0" max="100" value={invoiceForm.gst_percent}
                        onChange={e => setInvoiceForm(f => ({...f, gst_percent: e.target.value}))}
                        className="min-h-[40px] font-mono" data-testid="invoice-gst-input" />
                    </div>
                  </div>

                  {/* Live total preview */}
                  {invoiceForm.qty && invoiceForm.rate && (() => {
                    const q = parseFloat(invoiceForm.qty) || 0;
                    const r = parseFloat(invoiceForm.rate) || 0;
                    const g = parseFloat(invoiceForm.gst_percent) || 0;
                    const base = q * r;
                    const gst = base * g / 100;
                    return (
                      <div className="bg-[#F8FAFC] rounded p-2 text-xs font-mono space-y-0.5">
                        <div className="flex justify-between"><span>Subtotal</span><b>₹{base.toFixed(2)}</b></div>
                        <div className="flex justify-between"><span>GST</span><b>₹{gst.toFixed(2)}</b></div>
                        <div className="flex justify-between text-[#059669] font-bold text-sm border-t pt-1 mt-1"><span>TOTAL</span><span>₹{(base + gst).toFixed(2)}</span></div>
                      </div>
                    );
                  })()}

                  {/* Company override */}
                  <details className="border border-[#E2E8F0] rounded p-3">
                    <summary className="text-xs font-bold text-[#1D2951] cursor-pointer">Company Details (override / fill if blank)</summary>
                    <div className="mt-3 space-y-2">
                      <Input placeholder="Company Name" value={invoiceForm.company_name}
                        onChange={e => setInvoiceForm(f => ({...f, company_name: e.target.value}))}
                        className="min-h-[36px] font-semibold" />
                      <Input placeholder="Address" value={invoiceForm.company_address}
                        onChange={e => setInvoiceForm(f => ({...f, company_address: e.target.value}))}
                        className="min-h-[36px] text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="GSTIN" value={invoiceForm.company_gstin}
                          onChange={e => setInvoiceForm(f => ({...f, company_gstin: e.target.value}))}
                          className="min-h-[36px] text-sm font-mono" />
                        <Input placeholder="Phone" value={invoiceForm.company_phone}
                          onChange={e => setInvoiceForm(f => ({...f, company_phone: e.target.value}))}
                          className="min-h-[36px] text-sm" />
                      </div>
                      <Input placeholder="Email" value={invoiceForm.company_email}
                        onChange={e => setInvoiceForm(f => ({...f, company_email: e.target.value}))}
                        className="min-h-[36px] text-sm" />
                    </div>
                  </details>

                  {/* Notes */}
                  <div>
                    <Label className="text-xs font-semibold">Notes (optional)</Label>
                    <Input placeholder="PO ref, delivery instructions…" value={invoiceForm.notes}
                      onChange={e => setInvoiceForm(f => ({...f, notes: e.target.value}))}
                      className="min-h-[36px] text-sm" data-testid="invoice-notes-input" />
                  </div>

                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" onClick={() => setInvoiceDialog(null)} className="flex-1 min-h-[44px]">Cancel</Button>
                    <Button onClick={submitInvoice}
                      className="flex-1 bg-[#059669] hover:bg-[#047857] text-white min-h-[44px] font-bold" data-testid="invoice-submit-btn">
                      <Printer size={16} className="mr-2" /> Generate & Print
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Delete Password Dialog */}
          {deleteDialog && (
            <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
              <DialogContent className="max-w-sm" data-testid="delete-password-dialog">
                <DialogHeader><DialogTitle className="text-lg font-bold text-[#DC2626]">Delete Transaction</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-[#64748B]">
                    Deleting <span className="font-bold text-[#1D2951]">{deleteDialog.dataId}</span>. This will readjust all balances.
                  </p>
                  <div>
                    <Label className="text-xs font-semibold">Enter Password to confirm</Label>
                    <Input type="password" placeholder="Enter password" value={deletePassword}
                      onChange={e => setDeletePassword(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleDeleteConfirm(); }}
                      className="min-h-[44px] mt-1" data-testid="delete-password-input" autoFocus />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleDeleteConfirm} disabled={!deletePassword || deleting}
                      className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white min-h-[44px]" data-testid="delete-confirm-btn">
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </Button>
                    <Button variant="outline" onClick={() => setDeleteDialog(null)}
                      className="min-h-[44px]" data-testid="delete-cancel-btn">Cancel</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Return To Stock Dialog */}
          {returnDialog && (
            <Dialog open={!!returnDialog} onOpenChange={() => setReturnDialog(null)}>
              <DialogContent className="max-w-md" data-testid="return-to-stock-dialog">
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold text-[#7C3AED]">
                    <ArrowCounterClockwise size={20} className="inline mr-2" />Return To Stock
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-[#64748B]">
                    Returning stock from <span className="font-bold text-[#1D2951]">{opName}</span> — Transaction <span className="font-bold">{returnDialog.data_id}</span>
                  </p>
                  <div>
                    <Label className="text-xs font-semibold">Return Stock QTY *</Label>
                    <Input type="number" step="any" min="0" placeholder="Enter quantity"
                      value={returnQty} onChange={e => setReturnQty(e.target.value)}
                      className="min-h-[44px] mt-1 font-mono" data-testid="return-qty-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Return To *</Label>
                    <Select value={returnTo} onValueChange={setReturnTo}>
                      <SelectTrigger className="min-h-[44px] mt-1" data-testid="return-to-select">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent>
                        {liveOpFlow
                          .filter(o => o.key !== type)
                          .map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-[#F5F3FF] border border-[#C4B5FD] rounded">
                    <Checkbox checked={returnApprove} onCheckedChange={v => setReturnApprove(!!v)} data-testid="return-approve-checkbox" />
                    <Label className="text-xs font-bold text-[#5B21B6]">I approve this return</Label>
                  </div>
                  <Button onClick={handleReturnConfirm} disabled={!returnQty || !returnTo || !returnApprove}
                    className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white min-h-[44px]" data-testid="return-confirm-btn">
                    <CheckCircle size={18} className="mr-2" /> Confirm Return
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Approve Return Stock Dialog */}
          {approveReturnDialog && (
            <Dialog open={!!approveReturnDialog} onOpenChange={() => setApproveReturnDialog(null)}>
              <DialogContent className="max-w-md" data-testid="approve-return-dialog">
                <DialogHeader>
                  <DialogTitle className="text-lg font-bold text-[#059669]">Approve Return Stock?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="p-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded space-y-1">
                    <p className="text-sm"><span className="font-bold">Part:</span> {approveReturnDialog.part_no}</p>
                    <p className="text-sm"><span className="font-bold">Return From:</span> {OPERATION_NAMES[approveReturnDialog.source_operation] || approveReturnDialog.source_operation} ({approveReturnDialog.source_data_id || '-'})</p>
                    <p className="text-sm"><span className="font-bold">Return To:</span> {OPERATION_NAMES[approveReturnDialog.target_operation] || approveReturnDialog.target_operation} ({approveReturnDialog.target_data_id || '-'})</p>
                    <p className="text-sm"><span className="font-bold">Quantity:</span> <span className="font-mono font-bold text-[#7C3AED]">{approveReturnDialog.return_qty}</span></p>
                    <p className="text-sm"><span className="font-bold">Slip:</span> <span className="font-mono">{approveReturnDialog.slip_id || '-'}</span></p>
                    <p className="text-sm"><span className="font-bold">Requested by:</span> {approveReturnDialog.created_by}</p>
                  </div>
                  <p className="text-xs text-[#64748B]">Approving will subtract return qty from the original issued qty and subtract from the received side. All balances in both operations will be recalculated.</p>
                  <div className="flex gap-2">
                    <Button onClick={() => handleApproveReturn(approveReturnDialog.id)}
                      className="flex-1 bg-[#059669] hover:bg-[#047857] text-white min-h-[44px]" data-testid="approve-return-confirm-btn">
                      <Check size={18} className="mr-2" /> Approve
                    </Button>
                    <Button onClick={() => handleRejectReturn(approveReturnDialog.id)}
                      variant="outline" className="flex-1 min-h-[44px] text-[#DC2626] border-[#DC2626]" data-testid="reject-return-btn">
                      Reject
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Pending Returns Alert */}
          {pendingReturns.length > 0 && (
            <Card className="border-2 border-[#7C3AED] bg-[#F5F3FF] p-4" data-testid="pending-returns-card">
              <h3 className="text-sm font-bold text-[#5B21B6] mb-2" style={{fontFamily: 'Chivo'}}>
                <ArrowCounterClockwise size={16} className="inline mr-1" /> Pending Return Stock ({pendingReturns.length})
              </h3>
              <div className="space-y-2">
                {pendingReturns.map(ret => (
                  <div key={ret.id} className="flex items-center justify-between p-2 bg-white rounded border border-[#C4B5FD]">
                    <div className="text-xs">
                      <span className="font-bold text-[#1D2951]">{ret.part_no}</span>
                      <span className="mx-1 text-[#64748B]">|</span>
                      <span className="font-mono font-bold text-[#7C3AED]">{ret.return_qty} Nos</span>
                      <span className="mx-1 text-[#64748B]">from</span>
                      <span className="font-bold">{OPERATION_NAMES[ret.source_operation] || ret.source_operation}</span>
                      <span className="mx-1 text-[#64748B]">by</span>
                      <span>{ret.created_by}</span>
                    </div>
                    <Button size="sm" onClick={() => setApproveReturnDialog(ret)}
                      className="bg-[#059669] hover:bg-[#047857] text-white text-xs h-7 px-3" data-testid={`approve-return-${ret.id}`}>
                      Review
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Transaction History — Collapsible */}
          <Card className="border border-[#E2E8F0]">
            <div className="p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#1D2951]" style={{fontFamily: 'Chivo'}}>
                Transaction History {transactions.length > 0 ? `(${transactions.length})` : ''}
              </h3>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExportAllHistory}
                  className="text-xs border-[#059669] text-[#059669] hover:bg-[#ECFDF5]" data-testid="export-all-history-btn">
                  <DownloadSimple size={14} className="mr-1" /> Export All to Excel
                </Button>
                {transactions.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setHistoryExpanded(!historyExpanded)}
                    className="text-xs border-[#1D2951] text-[#1D2951]" data-testid="history-toggle">
                    {historyExpanded ? <><CaretDown size={14} className="mr-1" /> Collapse</> : <><CaretRight size={14} className="mr-1" /> Expand All ({transactions.length} entries)</>}
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="transactions-table">
                <thead className="bg-[#1D2951] text-white">{renderHistoryHeaders()}</thead>
                <tbody className="bg-white divide-y divide-[#E2E8F0]">
                  {/* Cumulative Summary — only when a part is selected */}
                  {formData.part_id && transactions.length > 0 && computeCumulativeSummary() && renderHistoryRow(computeCumulativeSummary(), 'sum', true)}
                  {/* When no part selected: show all rows (latest per part). When part selected & expanded: show individual entries */}
                  {(!formData.part_id ? transactions : (historyExpanded ? transactions : [])).map((t, idx) => renderHistoryRow(t, idx))}
                </tbody>
              </table>
              {transactions.length === 0 && <div className="text-center py-8 text-[#64748B] text-sm">No transactions recorded.</div>}
            </div>
          </Card>
        </div>

        {/* Issue To Operation Dialog */}
        {issueToDialog && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="issue-to-dialog">
            <Card className="w-[420px] p-6 shadow-xl">
              <h3 className="text-lg font-bold text-[#1D2951] mb-4" style={{fontFamily: 'Chivo'}}>
                <ArrowRight size={20} className="inline mr-2" /> Issue To Operation
              </h3>
              <p className="text-sm text-[#64748B] mb-4">
                Issuing <span className="font-bold text-[#1D2951]">{formData.issued_qty || 0} Nos</span> from <span className="font-bold">{opName}</span>.
                Select the target operation where this qty should go as Total Received.
              </p>
              <div className="mb-4">
                <Label className="text-xs font-semibold mb-1 block">Target Operation</Label>
                <Select value={issueToOperation} onValueChange={setIssueToOperation}>
                  <SelectTrigger className="min-h-[44px]" data-testid="issue-to-select">
                    <SelectValue placeholder="Select operation" />
                  </SelectTrigger>
                  <SelectContent>
                    {liveOpFlow
                      .filter(o => o.key !== type)
                      .map(o => (
                        <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleIssueToSubmit} disabled={!issueToOperation}
                  className="flex-1 bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]" data-testid="issue-to-confirm">
                  <CheckCircle size={18} className="mr-2" /> Confirm & Save
                </Button>
                <Button variant="outline" onClick={() => { setIssueToDialog(false); setIssueToOperation(""); }}
                  className="min-h-[44px]" data-testid="issue-to-cancel">Cancel</Button>
              </div>
            </Card>
          </div>
        )}

        {/* Rework To Operation Dialog */}
        {reworkToDialog && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="rework-to-dialog">
            <Card className="w-[420px] p-6 shadow-xl">
              <h3 className="text-lg font-bold text-[#F59E0B] mb-4" style={{fontFamily: 'Chivo'}}>
                Send Rework To
              </h3>
              <p className="text-sm text-[#64748B] mb-4">
                Sending <span className="font-bold text-[#F59E0B]">{formData.rework_qty || 0} Nos</span> for rework from <span className="font-bold">{opName}</span>.
                Select which operation should receive this material for rework.
              </p>
              <div className="mb-4">
                <Label className="text-xs font-semibold mb-1 block">Rework To Operation</Label>
                <Select value={reworkToOperation} onValueChange={setReworkToOperation}>
                  <SelectTrigger className="min-h-[44px]" data-testid="rework-to-select">
                    <SelectValue placeholder="Select operation" />
                  </SelectTrigger>
                  <SelectContent>
                    {liveOpFlow.map(o => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleReworkToSubmit} disabled={!reworkToOperation}
                  className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white min-h-[44px]" data-testid="rework-to-confirm">
                  Confirm & Continue
                </Button>
                <Button variant="outline" onClick={() => { setReworkToDialog(false); setReworkToOperation(""); }}
                  className="min-h-[44px]" data-testid="rework-to-cancel">Cancel</Button>
              </div>
            </Card>
          </div>
        )}

        {/* Outsource To Supplier Dialog (Semi-Firewall) */}
        {outsourceToDialog && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="outsource-to-dialog">
            <Card className="w-[420px] p-6 shadow-xl">
              <h3 className="text-lg font-bold text-[#92400E] mb-4" style={{fontFamily: 'Chivo'}}>
                Outsource To Supplier
              </h3>
              <p className="text-sm text-[#64748B] mb-4">
                Issuing <span className="font-bold text-[#92400E]">{formData.issued_qty || 0} Nos</span> from <span className="font-bold">{opName}</span> to external supplier.
              </p>
              <div className="space-y-3 mb-4">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Supplier *</Label>
                  <Select value={outsourceToSupplier} onValueChange={v => {
                    setOutsourceToSupplier(v);
                    const sup = suppliers.find(s => s.name === v);
                    setOutsourceToProcess((sup?.processes || [])[0] || "");
                  }}>
                    <SelectTrigger className="min-h-[44px]" data-testid="outsource-to-supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Process</Label>
                  <Select value={outsourceToProcess} onValueChange={setOutsourceToProcess}>
                    <SelectTrigger className="min-h-[44px]" data-testid="outsource-to-process-select">
                      <SelectValue placeholder="Process" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cutting">Cutting</SelectItem>
                      <SelectItem value="machining">Machining</SelectItem>
                      {(() => {
                        const sup = suppliers.find(s => s.name === outsourceToSupplier);
                        const procs = sup?.processes || [];
                        return (procs.length > 0 ? procs : outsourceProcessesList)
                          .map(p => <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>);
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Issued Quantity</Label>
                  <Input type="number" value={f2(formData.issued_qty)} readOnly className="min-h-[44px] font-mono bg-gray-100" />
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleOutsourceToSubmit} disabled={!outsourceToSupplier}
                  className="flex-1 bg-[#92400E] hover:bg-[#78350F] text-white min-h-[44px]" data-testid="outsource-to-confirm">
                  Confirm & Save
                </Button>
                <Button variant="outline" onClick={() => { setOutsourceToDialog(false); setOutsourceToSupplier(""); setOutsourceToProcess(""); }}
                  className="min-h-[44px]" data-testid="outsource-to-cancel">Cancel</Button>
              </div>
            </Card>
          </div>
        )}

        {/* Notification Sidebar — Incoming Issues */}
        {notifOpen && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="notification-sidebar-overlay">
            <div className="absolute inset-0 bg-black/40" onClick={() => setNotifOpen(false)} />
            <div className="relative w-[400px] max-w-[90vw] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
              <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#1D2951]">
                <div className="flex items-center gap-2">
                  <BellRinging size={20} weight="fill" className="text-[#F5DF4D]" />
                  <h2 className="text-base font-bold text-white" style={{fontFamily: 'Chivo'}}>Incoming Material</h2>
                  <span className="bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{incomingAll.filter(it => !dismissedNotifIds.has(it.id)).length}</span>
                </div>
                <button onClick={() => setNotifOpen(false)} className="p-1.5 rounded hover:bg-[#2A3B73] text-gray-300 hover:text-white" data-testid="close-notification-sidebar">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {(() => {
                  const visibleItems = incomingAll.filter(it => !dismissedNotifIds.has(it.id));
                  if (visibleItems.length === 0) {
                    return <div className="text-center py-10 text-[#94A3B8] text-sm">No incoming material pending</div>;
                  }
                  return visibleItems.map(item => {
                    const typeBadge = item.type === 'rework'
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#FFFBEB] text-[#D97706] border border-[#FDE68A]">REWORK</span>
                      : item.type === 'return'
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]">RETURN</span>
                      : item.source === 'raw_material'
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#FEF2F2] text-[#DC2626] border border-[#FCA5A5]">RM</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#EFF6FF] text-[#3B82F6] border border-[#BFDBFE]">ISSUED</span>;
                    return (
                    <div key={`${item.id}-${item.type || item.source}`} className={`p-3 rounded-lg border transition-colors ${selectedNotifs.has(item.id) ? 'bg-[#EFF6FF] border-[#3B82F6]' : 'bg-[#F8FAFC] border-[#E2E8F0] hover:border-[#CBD5E1]'}`}
                      data-testid={`notif-item-${item.id}`}>
                      <div className="flex items-start gap-2.5">
                        <Checkbox
                          checked={selectedNotifs.has(item.id)}
                          onCheckedChange={(v) => {
                            setSelectedNotifs(prev => {
                              const next = new Set(prev);
                              if (v) next.add(item.id); else next.delete(item.id);
                              return next;
                            });
                          }}
                          className="mt-0.5"
                          data-testid={`notif-check-${item.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-[#1D2951]">{item.part_no}</span>
                              {typeBadge}
                            </div>
                            <span className="text-xs font-mono font-bold text-[#059669]">{parseFloat(item.qty || 0).toFixed(2)} {item.source === "raw_material" ? "Kg" : "Nos"}</span>
                          </div>
                          <div className="text-[11px] text-[#64748B] mt-1 space-y-0.5">
                            <div>From: <span className="font-semibold text-[#475569]">{item.from_op}</span> {item.data_id && <span className="text-[#7C3AED]">({item.data_id})</span>}</div>
                            <div>Date: <span className="font-semibold text-[#475569]">{item.date}</span>
                              {item.created_at && <span className="ml-2 text-[#94A3B8]">{new Date(item.created_at).toLocaleTimeString()}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );});
                })()}
              </div>

              {incomingAll.filter(it => !dismissedNotifIds.has(it.id)).length > 0 && (
                <div className="p-3 border-t border-[#E2E8F0] bg-white space-y-2">
                  {(() => {
                    const visible = incomingAll.filter(it => !dismissedNotifIds.has(it.id));
                    return (
                      <>
                        <div className="flex items-center justify-between text-xs text-[#64748B]">
                          <button onClick={() => {
                            if (selectedNotifs.size === visible.length) setSelectedNotifs(new Set());
                            else setSelectedNotifs(new Set(visible.map(i => i.id)));
                          }} className="text-[#3B82F6] hover:underline font-semibold" data-testid="select-all-notifs">
                            {selectedNotifs.size === visible.length ? "Deselect All" : "Select All"}
                          </button>
                          <span>{selectedNotifs.size} selected</span>
                        </div>
                        <Button onClick={handleClearNotifications} disabled={selectedNotifs.size === 0}
                          className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white min-h-[40px] text-sm font-bold" data-testid="clear-selected-notifs">
                          <Check size={16} className="mr-2" /> Dismiss Selected ({selectedNotifs.size})
                        </Button>
                        <p className="text-[10px] text-[#94A3B8] text-center italic">Dismissing only hides the notification — the qty stays available in the new-entry form.</p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Return As Is Dialog — select where to return */}
        {returnAsIsDialog && (
          <Dialog open={returnAsIsDialog} onOpenChange={() => setReturnAsIsDialog(false)}>
            <DialogContent className="max-w-md" data-testid="return-as-is-dialog">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-[#059669]">
                  <ArrowCounterClockwise size={20} className="inline mr-2" />Return To
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-[#64748B]">Select which operation to return qty to. It will be added to that operation's <b>Return Stock</b>.</p>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Return To Operation *</Label>
                  <Select value={returnAsSelections.target_op || ""} onValueChange={v => setReturnAsSelections(prev => ({ ...prev, target_op: v }))}>
                    <SelectTrigger className="min-h-[44px]" data-testid="return-to-op-select"><SelectValue placeholder="Select operation" /></SelectTrigger>
                    <SelectContent>
                      {liveOpFlow.filter(o => o.key !== type).map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Quantity to Return *</Label>
                  <Input type="number" step="any" min="0" value={returnAsSelections.qty || ""}
                    onChange={e => setReturnAsSelections(prev => ({ ...prev, qty: e.target.value }))}
                    className="min-h-[44px] font-mono" data-testid="return-as-is-qty" />
                </div>
                <Button onClick={handleReturnAsIsConfirm} disabled={returnAsLoading || !returnAsSelections.target_op}
                  className="w-full bg-[#059669] hover:bg-[#047857] text-white min-h-[44px]" data-testid="return-as-is-confirm">
                  <CheckCircle size={18} className="mr-2" /> {returnAsLoading ? "Processing..." : "Confirm Return"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
        {smartDeleteTxnId && (
          <SmartDeleteModal
            open={!!smartDeleteTxnId}
            txnType="transaction"
            txnId={smartDeleteTxnId}
            onClose={() => setSmartDeleteTxnId(null)}
            onDeleted={() => { setSmartDeleteTxnId(null); fetchTransactions(); }}
          />
        )}
      </Layout>
    </OperationGuard>
  );
}

export default Operations;

/* ──────────────────────────── DispatchPOTable ──────────────────────────── */
const DispatchPOTable = forwardRef(function DispatchPOTable({ partId, available, onSumChange, editMode = false }, ref) {
  const fmt2 = (v) => { const n = parseFloat(v); return isNaN(n) ? '0.00' : n.toFixed(2); };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    if (!partId) { setRows([]); setEdits({}); return; }
    setLoading(true);
    try {
      // In edit mode we surface ALL PO rows (including fully-dispatched ones)
      // so the operator can see exactly what's allocated against each PO and
      // adjust if needed. In create mode we only show open rows for clarity.
      const r = await axios.get(`${API}/sales-register/open-for-part/${partId}`, {
        skipPeriod: true,
        params: editMode ? { include_all: true } : {},
      });
      setRows(r.data || []);
      setEdits({});
    } catch { /* keep silent */ }
    finally { setLoading(false); }
  }, [partId, editMode]);
  useEffect(() => { load(); }, [load]);

  // Compute current sum of Dispatch Quantity 2 across all open POs (per user spec).
  const sum = useMemo(() => rows.reduce((s, r) => {
    const cur = edits[r.id] === undefined ? r.dispatch_qty_2 : parseFloat(edits[r.id] || 0);
    return s + (isNaN(cur) ? 0 : cur);
  }, 0), [rows, edits]);

  // Push the live sum back up to the parent so it can drive the Dispatch Quantity field.
  useEffect(() => { if (onSumChange) onSumChange(sum); }, [sum, onSumChange]);

  const dirtyRows = rows.filter(r => edits[r.id] !== undefined && parseFloat(edits[r.id] || 0) !== r.dispatch_qty_2);

  // Validate any dirty row's value against ordered_qty.
  const validateLocal = () => {
    for (const r of dirtyRows) {
      const v = parseFloat(edits[r.id] || 0);
      if (isNaN(v) || v < 0) { toast.error(`PO ${r.po_no || r.id}: enter a valid number`); return false; }
      if (v > r.ordered_qty + 1e-9) { toast.error(`PO ${r.po_no || r.id}: Dispatch Quantity 2 (${v}) exceeds PO Quantity (${r.ordered_qty})`); return false; }
    }
    return true;
  };

  const saveAll = async () => {
    if (dirtyRows.length === 0) return { saved: 0 };
    if (!validateLocal()) throw new Error("PO validation failed");
    let saved = 0;
    for (const r of dirtyRows) {
      const v = parseFloat(edits[r.id] || 0);
      try {
        // Grouped rows (po_no + delivery_date matched multiple sales_register
        // entries) save via the group endpoint so the qty is distributed
        // across underlying rows. Standalone rows still use the per-row
        // endpoint for back-compat.
        if (r.member_ids && r.member_ids.length > 1) {
          await axios.post(`${API}/sales-register/dispatch-qty-2-group`,
            { member_ids: r.member_ids, dispatch_qty_2: v }, { skipPeriod: true });
        } else {
          const realId = (r.member_ids && r.member_ids[0]) || r.id;
          await axios.post(`${API}/sales-register/${realId}/dispatch-qty-2`,
            { dispatch_qty_2: v }, { skipPeriod: true });
        }
        saved += 1;
      } catch (e) {
        toast.error(`PO ${r.po_no || r.id}: ${e.response?.data?.detail || "save failed"}`);
        throw e;
      }
    }
    await load();
    return { saved };
  };

  // Expose methods + state to parent.
  useImperativeHandle(ref, () => ({
    saveAll,
    refresh: load,
    hasDirty: () => dirtyRows.length > 0,
  }), [dirtyRows, load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!partId) return null;

  // Visual hint when sum exceeds available stock.
  const sumExceeds = available !== undefined && sum > available + 1e-9;

  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="dispatch-po-table">
      <div className="bg-[#1D2951] text-white px-4 py-2 flex items-center justify-between">
        <span className="text-sm font-bold uppercase tracking-wider">Open POs — this part</span>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-[#F5DF4D] text-[#1D2951] rounded-full px-2 py-0.5 font-bold">{rows.length}</span>
          <Button size="sm" disabled={dirtyRows.length === 0}
            onClick={async () => {
              try { const r = await saveAll(); if (r.saved > 0) toast.success(`Saved ${r.saved} PO row(s)`); }
              catch { /* errors already toasted */ }
            }}
            className={`h-7 px-3 text-xs font-bold ${dirtyRows.length > 0 ? 'bg-[#059669] hover:bg-[#047857] text-white' : 'bg-[#475569] text-[#94A3B8] cursor-not-allowed'}`}
            data-testid="po-save-all-btn">
            Save POs ({dirtyRows.length})
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1F5F9] text-xs uppercase text-[#64748B]">
              <th className="px-3 py-2 text-left font-bold">PO Number</th>
              <th className="px-3 py-2 text-left font-bold">Delivery Date</th>
              <th className="px-3 py-2 text-right font-bold">PO Quantity</th>
              <th className="px-3 py-2 text-right font-bold text-[#059669]">Dispatch Quantity (v2)</th>
              <th className="px-3 py-2 text-right font-bold text-[#DC2626]">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center py-4 text-[#64748B]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-4 text-[#94A3B8] italic">No open POs — all caught up ✓</td></tr>
            ) : rows.map(r => {
              const cur = edits[r.id] === undefined ? r.dispatch_qty_2 : edits[r.id];
              const dirty = edits[r.id] !== undefined && parseFloat(edits[r.id] || 0) !== r.dispatch_qty_2;
              return (
                <tr key={r.id} className={`border-b border-[#E2E8F0] hover:bg-[#F8FAFC] ${dirty ? 'bg-[#FFFBEB]' : ''}`} data-testid={`po-row-${r.po_no || r.id}`}>
                  <td className="px-3 py-2 font-mono font-bold text-[#1D2951]">
                    {r.po_no || <span className="text-[#94A3B8]">—</span>}
                    {r.members > 1 && (
                      <span className="ml-1.5 align-middle inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-[#1D2951] text-[#F5DF4D]" title={`Clubbed from ${r.members} sales-register rows with the same PO No. + Delivery Date`}>
                        ×{r.members}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#475569] text-xs">{r.delivery_date || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt2(r.ordered_qty)}</td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="any" min="0" max={r.ordered_qty}
                      value={cur}
                      onChange={e => setEdits({ ...edits, [r.id]: e.target.value })}
                      className="h-8 text-sm text-right font-mono w-24 inline-block"
                      data-testid={`po-qty2-input-${r.po_no || r.id}`} />
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#DC2626]">{fmt2(r.ordered_qty - parseFloat(cur || 0))}</td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-[#F1F5F9] border-t-2 border-[#1D2951]">
                <td colSpan="3" className="px-3 py-2 text-right text-xs font-bold uppercase text-[#1D2951]">Total Dispatch Quantity 2</td>
                <td className={`px-3 py-2 text-right font-mono font-extrabold ${sumExceeds ? 'text-[#DC2626] bg-[#FEE2E2]' : 'text-[#059669]'}`} data-testid="po-sum-cell">{fmt2(sum)}</td>
                <td className="px-3 py-2 text-right text-[10px] text-[#64748B]">{available !== undefined && `Avail: ${fmt2(available)}`}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className={`text-[11px] italic px-4 py-2 border-t border-[#E2E8F0] ${sumExceeds ? 'bg-[#FEE2E2] text-[#7F1D1D] font-bold' : 'bg-[#F8FAFC] text-[#64748B]'}`}>
        {sumExceeds
          ? `⚠ Total Dispatch Quantity 2 (${fmt2(sum)}) exceeds Available Quantity (${fmt2(available)}). Reduce a PO row before saving.`
          : `Dispatch Quantity above is auto-filled from this total. Edits are saved when you click "Save POs" or when you save the dispatch transaction.`}
      </div>
    </div>
  );
});
